import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { ApiEndpoint } from '../types';
import TableViewer from './TableViewer';
import MonthYearPicker from './MonthYearPicker';
import DateRangePicker from './DateRangePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faRedo,
  faHourglassHalf,
  faTimes,
  faExclamationTriangle,
  faCheckCircle,
  faChartBar,
  faRocket,
  faClipboardList,
  faSave,
  faCog,
  faEdit,
  faUsers,
  faTrash,
  faLightbulb,
  faHome,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from '../shared/LoadingSpinner';

// Appends any raw fields the backend added later (not yet part of a report's explicit
// column order) to the end of the row, so new fields show up instead of being dropped.
const appendUnmappedFields = (orderedObject: any, rawItem: any, knownRawKeys: string[]): void => {
  if (!rawItem || typeof rawItem !== 'object') return;
  Object.keys(rawItem).forEach(key => {
    if (!knownRawKeys.includes(key)) {
      orderedObject[key] = rawItem[key];
    }
  });
};

const ApiExplorer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [endpoints, setEndpoints] = useState<{ [key: string]: ApiEndpoint[] }>({});
  const [endpointsLoaded, setEndpointsLoaded] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<ApiEndpoint | null>(null);

  const [dynamicParams, setDynamicParams] = useState<{ [key: string]: string | boolean }>({});
  const [response, setResponse] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [userTypes, setUserTypes] = useState<string[]>([]);
  
  // State to persist loaded data for each endpoint
  const [endpointDataCache, setEndpointDataCache] = useState<{
    [endpointName: string]: {
      data: any;
      params: { [key: string]: string | boolean };
      timestamp: string;
      error?: string;
    }
  }>({});

  // State to preserve the last entered filter params per endpoint
  // Used so that after "Clear Data" + "Load Data", reports with filters
  // reload using the previously entered filters (not current month defaults)
  const [lastEnteredParams, setLastEnteredParams] = useState<{
    [endpointName: string]: { [key: string]: string | boolean }
  }>({});

  // Add request cancellation support
  const [currentRequestController, setCurrentRequestController] = useState<AbortController | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<string>('');
  
  // State for multiple deposit transactions
  const [depositTransactions, setDepositTransactions] = useState<Array<{
    id: string;
    userId: string;
    transactionType: string;
    value: string;
    comment: string;
  }>>([]);

  // Ref for auto-scrolling to table data
  const tableViewRef = useRef<HTMLDivElement>(null);

  // Common parameter configurations for different endpoints
  const getParameterConfig = (endpointPath: string) => {

    const configs: { [key: string]: Array<{ name: string; label: string; type: string; placeholder?: string; required?: boolean; options?: Array<{value: string, label: string}>; withDay?: boolean }> } = {
      'Users Types': [
        { 
          name: 'name', 
          label: 'User Type', 
          type: 'select', 
          required: true,
          options: userTypes.length > 0 ? userTypes.map(userType => ({
            value: userType,
            label: userType
          })) : [
            { value: 'loading', label: 'Loading user types...' }
          ]
        },
      ],
      'Users Points': [
        // No parameters - loads all data automatically
      ],
      'Users': [
        // No parameters - loads all users data automatically
      ],
      'Users Data': [
        // No parameters - loads all users data automatically
      ],
      'Referral Users': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false },
        { name: 'dateType', label: 'Date Type', type: 'select', options: [{value: 'AchievedDate', label: 'Achieved Date'}, {value: 'referDate', label: 'Refer Date'}], required: false },
      ],
      'Referred Users': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false },
        { name: 'dateType', label: 'Date Type', type: 'select', options: [{value: 'AchievedDate', label: 'Achieved Date'}, {value: 'referDate', label: 'Refer Date'}], required: false },
      ],
      'User Details Per Product Per Action': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      
      'Warehouse Scan In/Out': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Scans per product per user type': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2024-01-01' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Scan Per Product details': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2023-01-01' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-12-31' },
      ],
      'Wallet Transactions': [
        { name: 'userId', label: 'User ID', type: 'text', placeholder: 'Enter user ID', required: false },
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false },
        { 
          name: 'transactionType', 
          label: 'Transaction Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'All', label: 'All' },
            { value: 'GIFTS', label: 'GIFTS' },
            { value: 'REFERING', label: 'REFERING' },
            { value: 'REWARD', label: 'REWARD' },
            { value: 'ENDOFMONTH', label: 'ENDOFMONTH' },
            { value: 'SCANOUT', label: 'SCANOUT' },
            { value: 'SCANIN', label: 'SCANIN' },
            { value: 'CASHINCENTIVE', label: 'CASHINCENTIVE' },
            { value: 'EXTRAINCENTIVE', label: 'EXTRAINCENTIVE' },
            { value: 'RECIEVETRANSFEREDINCENTIVE', label: 'RECIEVETRANSFEREDINCENTIVE' },
            { value: 'SCANUSE', label: 'SCANUSE' }
          ]
        },
      ],
      // Additional QR Codes endpoints
      'Deactivated Incentive QR': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Configuration Result Per Product': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Order Scanout From Warehouse': [
        { name: 'startDate', label: 'Start Date', type: 'date', required: true, placeholder: '2025-05-20' },
        { name: 'endDate', label: 'End Date', type: 'date', required: true, placeholder: '2025-05-20' },
      ],
      'Warehouse Quantity Aggregation': [
        // No parameters - loads all data automatically
      ],
      // Wallet endpoints
      'Deliver Cash': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false },
        // No parameters - take supplier name
      ],
      'Deliver Transfer': [
        // No parameters - loads all data automatically
      ],
      'Welcome Bonus': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false, withDay: true }
      ],
      // Payment/Paymob endpoints
      'Paymob': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false }
      ],
      // Invoice endpoints
      'invoice-photos': [
        // No parameters - loads all data automatically
      ],
      // Transaction Logs endpoints
      'reset-logs': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false, withDay: true }
      ],
      // Joining Requests endpoints
      'joining-requests': [
        // No parameters - loads all data automatically
      ],
      // National IDs endpoints
      'National IDs': [
        // No parameters - loads all data automatically
      ],
      // Return System endpoints
      'Return System': [
        // { 
        //   name: 'dateType', 
        //   label: 'Date Type', 
        //   type: 'select', 
        //   required: false,
        //   options: [
        //     { value: 'Return Date', label: 'Return Date' },
        //     { value: 'Transaction Date', label: 'Transaction Date' }
        //   ]
        // },
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false }
      ],
      'Incentive': [
        { 
          name: 'dateType', 
          label: 'Date Type', 
          type: 'select', 
          required: false,
          options: [
            { value: 'Return Date', label: 'Return Date' },
            { value: 'Transaction Date', label: 'Transaction Date' }
          ]
        },
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false, withDay: true }
      ],
      // Deposit Amount endpoints
      'Deposit Amount': [
        // No individual parameters - uses transaction table
      ],
      'Deposit Amount Log': [
      ],
      'Gifts': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false }
      ],
      'End Of Month': [
        { name: 'month', label: 'Select Date', type: 'monthYear', required: false }
      ],
    };
    
    return configs[endpointPath] || [];
    // Default fallback removed to prevent errors with undefined endpoints
  };

  const updateDynamicParams = (name: string, value: string | boolean) => {
    setDynamicParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Functions for managing deposit transactions
  const addDepositTransaction = () => {
    const newTransaction = {
      id: `tx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: '',
      transactionType: '',
      value: '',
      comment: ''
    };
    setDepositTransactions(prev => {
      const newTransactions = [...prev, newTransaction];
      return newTransactions;
    });
  };

  const updateDepositTransaction = (id: string, field: string, value: string) => {
    setDepositTransactions(prev => {
      const updated = prev.map(tx => tx.id === id ? { ...tx, [field]: value } : tx);
      return updated;
    });
  };

  const removeDepositTransaction = (id: string) => {
    setDepositTransactions(prev => prev.filter(tx => tx.id !== id));
  };

  const clearAllDepositTransactions = () => {
    setDepositTransactions([]);
  };

  const convertDynamicParamsToJson = (overrideParams?: { [key: string]: string | boolean }) => {
    const paramsWithDefaults = { ...(overrideParams ?? dynamicParams) };

    const filtered = Object.entries(paramsWithDefaults)
      .filter(([key, value]) => {
        if (typeof value === 'boolean') return true;
        if (typeof value === 'string') return value.trim() !== '';
        return false;
      })
      .reduce((acc, [key, value]) => {
        // Handle boolean values
        if (typeof value === 'boolean') {
          acc[key] = value;
        } else if (value === 'true') {
          acc[key] = true;
        } else if (value === 'false') {
          acc[key] = false;
        } else if (typeof value === 'string' && /^\d+$/.test(value)) {
          // Try to parse as number if it looks like one
          acc[key] = parseInt(value);
        } else if (typeof value === 'string' && /^\d+\.\d+$/.test(value)) {
          acc[key] = parseFloat(value);
        } else if (typeof value === 'string' && (key.toLowerCase().includes('date') || key === 'from' || key === 'to')) {
          // Handle date formatting - ensure consistent YYYY-MM-DD format
          let dateValue = value;
          
          // Convert from YYYY/MM/DD to YYYY-MM-DD format
          if (dateValue.includes('/')) {
            const parts = dateValue.split('/');
            if (parts.length === 3) {
              dateValue = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            }
          }
          
          // Validate and format the date
          const dateObj = new Date(dateValue);
          if (!isNaN(dateObj.getTime())) {
            // Format as YYYY-MM-DD
            const year = dateObj.getFullYear();
            const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
            const day = dateObj.getDate().toString().padStart(2, '0');
            acc[key] = `${year}-${month}-${day}`;
          } else {
            acc[key] = value; // Keep original if parsing fails
          }
        } else {
          acc[key] = value;
        }
        return acc;
      }, {} as any);
    
    return JSON.stringify(filtered, null, 2);
  };

  // Handle endpoint selection and preserve/restore data
  const handleEndpointSelection = (endpoint: ApiEndpoint) => {
    // Cancel any existing request when switching endpoints
    if (currentRequestController) {
      currentRequestController.abort();
      setCurrentRequestController(null);
    }
    
    // Save current endpoint data to cache if we have data loaded
    if (selectedEndpoint && (response || error)) {
      setEndpointDataCache(prev => ({
        ...prev,
        [selectedEndpoint.endpoint]: {
          data: response,
          params: { ...dynamicParams },
          timestamp: new Date().toISOString(),
          error: error || undefined
        }
      }));
    }
    
    setSelectedEndpoint(endpoint);
    
    // Check if we have cached data for this endpoint
    const cachedData = endpointDataCache[endpoint.endpoint];
    if (cachedData) {
      // Restore cached data
      setResponse(cachedData.data);
      setDynamicParams(cachedData.params);
      setError(cachedData.error || '');
    } else {
      // Clear data for new endpoint
      setResponse(null);
      setError('');
      setDynamicParams({});
      setDepositTransactions([]); // Clear deposit transactions when switching endpoints
    }
    
    setLoading(false);
  };

  // Clear dynamic parameters when endpoint changes
  useEffect(() => {
    if (selectedEndpoint) {
      setDynamicParams({});
    }
  }, [selectedEndpoint]);

  const shouldAutoLoadEndpoint = (endpoint: ApiEndpoint): boolean => {
    if (!endpoint) return false;
    const endpointPath = endpoint.endpoint;

    // Do not auto-load operation-only endpoints
    if (endpointPath === 'Deposit Amount') {
      return false;
    }

    const paramConfig = getParameterConfig(endpointPath);
    if (!paramConfig || paramConfig.length === 0) {
      return true;
    }

    // Allow auto-load if all required parameters are date/monthYear values only
    return !paramConfig.some(param => {
      const isAutoFillable = param.type === 'date' || param.type === 'monthYear'
        || (endpointPath === 'Users Types' && param.name === 'name');
      return param.required && !isAutoFillable;
    });
  };

  useEffect(() => {
    if (selectedEndpoint && !response && !loading && shouldAutoLoadEndpoint(selectedEndpoint)) {
      executeApi();
    }
  }, [selectedEndpoint]);

  // Format Users Points data similar to Google Apps Script
  const formatUsersPointsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatUsersPointsData: Expected array, got:', typeof data);
      return [];
    }
    
    return data.map((user, index) => {
      try {
        return {
          'User ID': user?.userId || '',
          'Energya ID': user?.firebaseUserId || '',
          'First Name': user?.firstName || '',
          'Last Name': user?.lastName || '',
          'Phone': user?.phone || '',
          'Shop Name': user?.shopName || '',
          'Governorate': user?.governorate?.name || '',
          'District': user?.district?.name || '',
          'User Type': user?.userType?.name || '',
          'Segment': user?.segment?.name || '',
          'Total Points': user?.totalpoints || 0,
          'Total Money': user?.totalmoney || 0,
          'Total Wheels Points': user?.totalWheelspoints || 0,
          'Date': user?.createdAt ? new Date(user.createdAt).toLocaleString() : '',
          'Parent ID': user?.parentProfile?.userId || '',
          'Latitude': user?.coordinates?.[0] || '',
          'Longitude': user?.coordinates?.[1] || '',
          'First Scan Date': user?.firstScanDate ? new Date(user.firstScanDate).toLocaleString() : ''
        };
      } catch (error) {
        console.error(`Error formatting user data at index ${index}:`, error, user);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format user data'
        };
      }
    });
  }, []);

  // Format Master Data (Users Data) based on the /UsersData API response
  const formatMasterDataData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatMasterDataData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the /UsersData API response - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'Full Name',
      'Phone',
      'Segment',
      'Governorate',
      'District',
      'Latitude',
      'Longitude',
      'Shop Name',
      'Shop Image',
      'Parent User ID',
      'Parent User Name',
      'Joining Date',
      'Number of Months Since Registration',
      'Status',
      'First Scan Date',
      'Last Scan Date',
      'Total Scans',
      'Total Wheel Points',
      'Total Money',
      'Verified Account',
      'Verification Date',
      'Verified By Name',
      'Verified By User ID',
      'Accepted By Name',
      'Accepted By User ID',
      'Suspended At',
      'Suspended By Name',
      'Suspended By User ID',
      'Suspension Reason',
      'Reactivated At',
      'Reactivated By Name',
      'Reactivated By User ID',
      'User Comment',
      'Promoter Name',
      'Total Redeemed Value',
      'Total Manual Redeemed Value',
      'Total paymop Redeemed Value',
      'Total Active Months',
      'Total Points',
      'Total Tons'
    ];

    return extractedData.map((userData, index) => {
      try {
        const orderedObject: any = {};

        // API already returns human-readable keys; just re-map into the desired order
        const dataMapping: { [key: string]: any } = {
          'User Type': userData?.['User Type'] ?? '-',
          'User ID': userData?.['User ID'] ?? '-',
          'Full Name': userData?.['Full Name'] ?? '-',
          'Phone': userData?.['Phone'] ?? '_',
          'Segment': userData?.['Segment'] ?? '_',
          'Governorate': userData?.['Governorate'] ?? '_',
          'District': userData?.['District'] ?? '_',
          'Latitude': userData?.['Latitude'] ?? '_',
          'Longitude': userData?.['Longitude'] ?? '_',
          'Shop Name': userData?.['Shop Name'] ?? '_',
          'Shop Image': userData?.['shop image'] ?? '_',
          'Parent User ID': userData?.['Parent User ID'] ?? '_',
          'Parent User Name': userData?.['Parent User Name'] ?? '_',
          'Joining Date': userData?.['Joining Date'] ?? '_',
          'Number of Months Since Registration': userData?.['Number of Months Since Registration'] ?? '_',
          'Status': userData?.['Status'] ?? '_',
          'First Scan Date': userData?.['First Scan Date'] ?? '_',
          'Last Scan Date': userData?.['Last Scan Date'] ?? '_',
          'Total Scans': userData?.['Total Scans'] ?? '_',
          'Total Wheel Points': userData?.['Total Wheel Points'] ?? '_',
          'Total Money': userData?.['Total Money'] ?? '_',
          'Verified Account': userData?.['Verified Account'] ?? '_',
          'Verification Date': userData?.['Verification Date'] ?? '_',
          'Verified By Name': userData?.['Verified By Name'] ?? '_',
          'Verified By User ID': userData?.['Verified By User ID'] ?? '_',
          'Accepted By Name': userData?.['Accepted By Name'] ?? '_',
          'Accepted By User ID': userData?.['Accepted By User ID'] ?? '_',
          'Suspended At': userData?.['Suspended At'] ?? '_',
          'Suspended By Name': userData?.['Suspended By Name'] ?? '_',
          'Suspended By User ID': userData?.['Suspended By User ID'] ?? '_',
          'Suspension Reason': userData?.['Suspension Reason'] ?? '_',
          'Reactivated At': userData?.['Reactivated At'] ?? '_',
          'Reactivated By Name': userData?.['Reactivated By Name'] ?? '_',
          'Reactivated By User ID': userData?.['Reactivated By User ID'] ?? '_',
          'User Comment': userData?.['User Comment'] ?? '_',
          'Promoter Name': userData?.['Promoter Name'] ?? '_',
          'Total Redeemed Value': userData?.['Total Redeemed Value'] ?? '_',
          'Total Manual Redeemed Value': userData?.['Total Manual Redeemed Value'] ?? '_',
          'Total paymop Redeemed Value': userData?.['Total paymop Redeemed Value'] ?? '_',
          'Total Active Months': userData?.['Total Active Months'] ?? '_',
          'Total Points': userData?.['Total Points'] ?? '_',
          'Total Tons': userData?.['Total Tons'] ?? '_'
        };

        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        const knownRawKeys = columnOrder.map(column => column === 'Shop Image' ? 'shop image' : column);
        appendUnmappedFields(orderedObject, userData, knownRawKeys);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting master data at index ${index}:`, error, userData);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format master data'
        };
      }
    });
  }, []);

  // Format Users Types data from docs array
  const formatUsersTypesData = useCallback((data: any): any[] => {
    // Handle nested structure with docs array
    let extractedData = data;
    
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.docs && Array.isArray(data.docs)) {
        extractedData = data.docs;
      } else if (Array.isArray(data.data)) {
        extractedData = data.data;
      }
    }
    
    if (!Array.isArray(extractedData)) {
      console.warn('formatUsersTypesData: Expected array, got:', typeof extractedData);
      return [];
    }
    
    // Define the exact column order as it should appear in the table
    const columnOrder = [
      'User ID',
      'First Name',
      'Last Name',
      'Phone',
      'User Type Name',
      'Shop Name',
      'District Data',
      'Governorate Data',
      'Segment Name',
      'Date',
      'Firebase ID',
      'Parent Profile ID',
      'Latitude',
      'Longitude',
      'Shop Image',
      'ID'
    ];
    
    return extractedData.map((user, index) => {
      try {
        const orderedObject: any = {};
        
        const dataMapping: { [key: string]: any } = {
          'User ID': user?.userId || '-',
          'First Name': user?.firstName || '-',
          'Last Name': user?.lastName || '-',
          'Phone': user?.phone || '-',
          'User Type Name': user?.userTypeName || '-',
          'Shop Name': user?.shopName || '-',
          'District Data': user?.districtData || '-',
          'Governorate Data': user?.governorateData || '-',
          'Segment Name': user?.segmentName || '-',
          'Date': user?.Date ? new Date(user.Date).toLocaleString() : '-',
          'Firebase ID': user?.firebaseUserId || '-',
          'Parent Profile ID': user?.parentprofileUserId || '-',
          'Latitude': user?.latitude !== null && user?.latitude !== undefined ? user.latitude : '-',
          'Longitude': user?.longitude !== null && user?.longitude !== undefined ? user.longitude : '-',
          'Shop Image': user?.shopImage || '-',
          'ID': user?._id || '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting users types data at index ${index}:`, error, user);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format users types data'
        };
      }
    });
  }, []);

  // Format Referral Users data similar to Google Apps Script
  const formatReferralUsersData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReferralUsersData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define the exact column order as it should appear in the table
    const columnOrder = [
      'User Type',
      'User ID',
      'First Name',
      'Last Name',
      'Phone',
      'Governorate',
      'District',
      'Referred User Type',
      'Referred ID',
      'Referred Name',
      'Referred Phone',
      'Referred Governorate',
      'Referred District',
      'Transaction Reference',
      'Referral Mechanism',
      'Referral Incentive',
      'Referred Date',
      'Referred Time',
      'Achieved Date'
    ];
    
    return data.map((referral, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns in exact order
        const dataMapping: { [key: string]: any } = {
          'Transaction Reference': referral?._id || '-',
          'User Type': referral?.userType || '-',
          'User ID': referral?.userId || '-',
          'First Name': referral?.firstName || '-',
          'Last Name': referral?.lastName || '-',
          'Phone': referral?.phone || '-',
          'Governorate': referral?.governorate || '-',
          'District': referral?.district || '-',
          'Referred Date': referral?.date || '-',
          'Referred Time': referral?.time || '-',
          'Referred User Type': referral?.Referred?.userType || '-',
          'Referred ID': referral?.Referred?.userId || '-',
          'Referred Name': referral?.Referred?.userName || '-',
          'Referred Phone': referral?.Referred?.phone || '-',
          'Referred Governorate': referral?.Referred?.governorate || '-',
          'Referred District': referral?.Referred?.district || '-',
          'Referral Mechanism': referral?.referalModelType?.name || '-',
          'Referral Incentive': referral?.wallettransaction?.value || '-',
          'Achieved Date': referral?.wallettransaction?.updatedAt || '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, referral, [
          '_id', 'userType', 'userId', 'firstName', 'lastName', 'phone', 'governorate',
          'district', 'date', 'time', 'Referred', 'referalModelType', 'wallettransaction'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting referral data at index ${index}:`, error, referral);
        return {
          'Transaction Reference': `Error_${index}`,
          'Error': 'Failed to format referral data'
        };
      }
    });
  }, []);

  // Format Referred Users data based on Apps Script function structure
  const formatReferredUsersData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReferredUsersData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script referredUsers function
    const columnOrder = [
      'Transaction ID',
      'Referring ID',
      'Referring Name',
      'Referred ID',
      'Referred Name',
      'Referral Model Type',
      'Incentive Status',
      'Min Consumption',
      'Referral Reward Value',
      'Percentage Per Scan',
      'Referral Period',
      'Total Points',
      'Value',
      'Total Money',
      'Refer Date'
    ];
    
    return data.map((referredUser, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Transaction ID': referredUser?._id || '-',
          'Referring ID': referredUser?.referringId || '-',
          'Referring Name': referredUser?.referringName || '-',
          'Referred ID': referredUser?.referredId || '-',
          'Referred Name': referredUser?.referredName || '-',
          'Referral Model Type': referredUser?.referalModelTypeName || '-',
          'Incentive Status': referredUser?.isTakeIncentive ? 'pending' : 'given',
          'Min Consumption': referredUser?.minConsumption || '-',
          'Referral Reward Value': referredUser?.referralRewardValue || '-',
          'Percentage Per Scan': referredUser?.percentagePerScan || '-',
          'Referral Period': referredUser?.referralPeriod || '-',
          'Total Points': referredUser?.totalpoints || '-',
          'Value': referredUser?.value || '-',
          'Total Money': referredUser?.totalmoney || '-',
          'Refer Date': referredUser?.referDate || '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting referred user data at index ${index}:`, error, referredUser);
        return {
          'Transaction ID': `Error_${index}`,
          'Error': 'Failed to format referred user data'
        };
      }
    });
  }, []);

  // Format Scan Per Product Details data based on Apps Script function
  const formatScanPerProductDetailsData = useCallback((data: any): any[] => {
    // Handle nested API response structure like the Apps Script
    let extractedData = data;
    
    // Extract docs array from the response structure
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.docs && Array.isArray(data.docs)) {
        extractedData = data.docs;
    }
    }
    if (!Array.isArray(extractedData)) {
      console.warn('formatScanPerProductDetailsData: Expected array, got:', typeof extractedData);
      return [];
    }
    
    // Define columns based on the actual API response structure
    const columnOrder = [
      'QR Value',
      'QR Type', 
      'Wallet Type',
      'Scan Action',
      'Product Name',
      'Product Code',
      'Product Type Name',
      'User Type Name',
      'User ID',
      'User Name',
      'Phone',
      'Segment',
      'District',
      'Governorate',
      'Shop Name',
      'Purchase Points',
      'Purchase Cash',
      'Sells Points',
      'Sells Cash',
      'Use Points',
      'Use Cash',
      'Backword Points',
      'Backword Money',
      'Wheel Points',
      'Created At'
    ];
    
    return extractedData.map((scanData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};
        
        // Map data to columns based on actual API response structure
        const dataMapping: { [key: string]: any } = {
          'QR Value': scanData?.qrValue || '_',
          'QR Type': scanData?.qrType || '_',
          'Wallet Type': scanData?.WalletType || '_',
          'Scan Action': scanData?.scanAction || '_',
          'Product Name': scanData?.productName || '-',
          'Product Code': scanData?.productCode || '-',
          'Product Type Name': scanData?.productTypeName || '_',
          'User Type Name': scanData?.userTypeName || '-',
          'User ID': scanData?.userId || '_',
          'User Name': scanData?.userName || '_',
          'Phone': scanData?.phone || '_',
          'Segment': scanData?.segment?.name || '_',
          'District': scanData?.district || '_',
          'Governorate': scanData?.governorate || '_',
          'Shop Name': scanData?.shopName || '_',
          'Purchase Points': scanData?.purchasePoints || '_',
          'Purchase Cash': scanData?.purchaseCash || '_',
          'Sells Points': scanData?.sellsPoints || '_',
          'Sells Cash': scanData?.sellsCash || '_',
          'Use Points': scanData?.usePoints || '_',
          'Use Cash': scanData?.useCash || '_',
          'Backword Points': scanData?.backwordPoints || '_',
          'Backword Money': scanData?.backwordMoney || '_',
          'Wheel Points': scanData?.wheelPoints || '_',
          'Created At': scanData?.createdAt || '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting scan per product details data at index ${index}:`, error, scanData);
        return {
          'QR Value': `Error_${index}`,
          'Error': 'Failed to format scan per product details data'
        };
      }
    });
  }, []);

  // Format Scans per product per user type data based on Apps Script function
  const formatScansPerProductPerUserTypeData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatScansPerProductPerUserTypeData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script scansDetailsPerUserType function
    const columnOrder = [
      'Product Name',
      'Product Code',
      'User Type Name',
      'Scan Action',
      'Counter',
      'Product Type Name'
    ];
    
    return data.map((scanData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Product Name': scanData?.productName ?? '-',
          'Product Code': scanData?.productCode ?? '-',
          'User Type Name': scanData?.userTypeName ?? '_',
          'Scan Action': scanData?.scanAction ?? '_',
          'Counter': scanData?.counter ?? '_',
          'Product Type Name': scanData?.productTypeName ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting scan details data at index ${index}:`, error, scanData);
        return {
          'Product Name': `Error_${index}`,
          'Error': 'Failed to format scan details data'
        };
      }
    });
  }, []);

  // Format Orders From Warehouse (Warehouse Scan In/Out) based on the /qr-code/warehouseScanInOutDetails API response
  const formatWarehouseScanInOutData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatWarehouseScanInOutData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the /qr-code/warehouseScanInOutDetails API response - exact order
    const columnOrder = [
      'Warehouse ID',
      'Warehouse Name',
      'Warehouse User Name',
      'Product Name',
      'Product Type Name',
      'Image URL',
      'Order Value',
      'Counter',
      'Aggregation Date',
      'Stamped For User Type',
      'Stamped For User ID',
      'Stamped For User Name',
      'Scan Status',
      'Scanned By User Type',
      'Scanned By User ID',
      'Scanned By User Name',
      'Scanned By Scan Action',
      'User Scanned Date'
    ];

    return extractedData.map((scanData, index) => {
      try {
        const orderedObject: any = {};

        // API already returns human-readable keys; just re-map into the desired order
        const dataMapping: { [key: string]: any } = {
          'Warehouse ID': scanData?.['Warehouse ID'] ?? '-',
          'Warehouse Name': scanData?.['Warehouse Name'] ?? '_',
          'Warehouse User Name': scanData?.['Warehouse User Name'] ?? '_',
          'Product Name': scanData?.['Product Name'] ?? '_',
          'Product Type Name': scanData?.['Product Type Name'] ?? '_',
          'Image URL': scanData?.['Image URL'] ?? '_',
          'Order Value': scanData?.['Order Value'] ?? '_',
          'Counter': scanData?.['Counter'] ?? '_',
          'Aggregation Date': scanData?.['Aggregation Date'] ?? '_',
          'Stamped For User Type': scanData?.['Stamped For User Type'] ?? '_',
          'Stamped For User ID': scanData?.['Stamped For User ID'] ?? '_',
          'Stamped For User Name': scanData?.['Stamped For User Name'] ?? '_',
          'Scan Status': scanData?.['Scan Status'] ?? '_',
          'Scanned By User Type': scanData?.['Scanned By User Type'] ?? '_',
          'Scanned By User ID': scanData?.['Scanned By User ID'] ?? '_',
          'Scanned By User Name': scanData?.['Scanned By User Name'] ?? '_',
          'Scanned By Scan Action': scanData?.['Scanned By Scan Action'] ?? '_',
          'User Scanned Date': scanData?.['User Scanned Date'] ?? '_'
        };

        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, scanData, columnOrder);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting warehouse scan data at index ${index}:`, error, scanData);
        return {
          'Warehouse ID': `Error_${index}`,
          'Error': 'Failed to format warehouse scan data'
        };
      }
    });
  }, []);

  // Format Configuration Result Per Product data based on Apps Script function
  const formatConfigurationResultPerProductData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatConfigurationResultPerProductData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script configuration function
    const columnOrder = [
      'ID',
      'Product Name',
      'Product Code',
      'Configure Counter',
      'Product Type Name'
    ];
    
    return data.map((configData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': configData?._id ?? '-',
          'Product Name': configData?.productName ?? '-',
          'Product Code': configData?.productCode ?? '-',
          'Configure Counter': configData?.configureCounter ?? '_',
          'Product Type Name': configData?.productTypeName ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting configuration data at index ${index}:`, error, configData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format configuration data'
        };
      }
    });
  }, []);

  // Format Deactivated Incentive QR data based on Apps Script function
  const formatDeactivatedIncentiveQRData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatDeactivatedIncentiveQRData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script deactivatedIncentiveQR function
    // Order matches the exact sequence from the Apps Script: QRCode, QRType, disableIncentiveType, OrderCode, SKUCode, SKUName, WarehouseID, WarehouseName, UserName, createdAt date, createdAt time
    const columnOrder = [
      'QR Code',
      'QR Type', 
      'Disable Incentive Type',
      'Order Code',
      'SKU Code',
      'SKU Name',
      'Warehouse ID',
      'Warehouse Name',
      'User Name',
      'Created Date',
      'Created Time'
    ];
    
    return data.map((qrData, index) => {
      try {
        const orderedObject: any = {};
        
        // Format date and time similar to Apps Script
        let createdDate = '_';
        let createdTime = '_';
        
        if (qrData?.createdAt) {
          try {
            const dateObj = new Date(qrData.createdAt);
            createdDate = dateObj.toLocaleDateString();
            createdTime = dateObj.toLocaleTimeString();
          } catch (dateError) {
            console.warn('Error formatting date:', dateError);
            createdDate = '_';
            createdTime = '_';
          }
        }
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'QR Code': qrData?.QRCode ?? '-',
          'QR Type': qrData?.QRType ?? '-',
          'Disable Incentive Type': qrData?.disableIncentiveType ?? '_',
          'Order Code': qrData?.OrderCode ?? '_',
          'SKU Code': qrData?.SKUCode ?? '_',
          'SKU Name': qrData?.SKUName ?? '_',
          'Warehouse ID': qrData?.WarehouseID ?? '_',
          'Warehouse Name': qrData?.WarehouseName ?? '_',
          'User Name': qrData?.UserName ?? '_',
          'Created Date': createdDate,
          'Created Time': createdTime
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting deactivated incentive QR data at index ${index}:`, error, qrData);
        return {
          'QR Code': `Error_${index}`,
          'Error': 'Failed to format deactivated incentive QR data'
        };
      }
    });
  }, []);

  // Format Order Scanout From Warehouse data based on Apps Script function
  const formatOrderScanoutFromWarehouseData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatOrderScanoutFromWarehouseData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script orderScanoutFromWarehouse function
    const columnOrder = [
      'Value',
      'Warehouse Details',
      'Scaned',
      'Date'
    ];
    
    return data.map((scanData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Value': scanData?.value ?? '-',
          'Warehouse Details': scanData?.warehouseDetails ?? '-',
          'Scaned': scanData?.SCANED ?? '_',
          'Date': scanData?.date ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting order scanout data at index ${index}:`, error, scanData);
        return {
          'Value': `Error_${index}`,
          'Error': 'Failed to format order scanout data'
        };
      }
    });
  }, []);

  // Format Warehouse Quantity Aggregation data based on Apps Script function
  const formatWarehouseQuantityAggregationData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatWarehouseQuantityAggregationData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script warehouseQuantityAggregation function
    const columnOrder = [
      'Warehouse ID',
      'Warehouse Name',
      'Warehouse User Name',
      'SKU Name',
      'Quantity',
      'Delivery Note QR Code',
      'Stamped For User ID',
      'Stamped For User Name',
      'Stamped User Type',
      'Scanned',
      'Scan Date Time',
      'Scan User ID',
      'Scan Performed By',
      'Scan User Type',
      'Aggregation Date Time'
    ];
    
    return data.map((warehouseData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'Warehouse ID': warehouseData?.warehouseId ?? '-',
          'Warehouse Name': warehouseData?.warehouseName ?? '_',
          'Warehouse User Name': warehouseData?.warehouseUserName ?? '_',
          'SKU Name': warehouseData?.skuName ?? '_',
          'Quantity': warehouseData?.quantity ?? '_',
          'Delivery Note QR Code': warehouseData?.deliveryNoteQrCode ?? '_',
          'Stamped For User ID': warehouseData?.stampedForUserId ?? '_',
          'Stamped For User Name': warehouseData?.stampedForUserName ?? '_',
          'Stamped User Type': warehouseData?.stampedUserType ?? '_',
          'Scanned': warehouseData?.scanned ?? '_',
          'Scan Date Time': warehouseData?.scanDateTime ?? '_',
          'Scan User ID': warehouseData?.scanUserId ?? '_',
          'Scan Performed By': warehouseData?.scanPerformedBy ?? '_',
          'Scan User Type': warehouseData?.scanUserType ?? '_',
          'Aggregation Date Time': warehouseData?.aggregationDateTime ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting warehouse quantity aggregation data at index ${index}:`, error, warehouseData);
        return {
          'Warehouse ID': `Error_${index}`,
          'Error': 'Failed to format warehouse quantity aggregation data'
        };
      }
    });
  }, []);

  // Format Paymob data based on Apps Script function
  const formatPaymobData = useCallback((data: any): any[] => {
    // Handle nested API response structure like the Apps Script
    let extractedData = data;
    
    // Extract docs array from the response structure
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.docs && Array.isArray(data.docs)) {
        extractedData = data.docs;
      } else if (Array.isArray(data.data)) {
        extractedData = data.data;
      } else if (Array.isArray(data.body)) {
        extractedData = data.body;
      }
    }
    
    if (!Array.isArray(extractedData)) {
      console.warn('formatPaymobData: Expected array, got:', typeof extractedData);
      return [];
    }
    
    // Define columns based on the Apps Script paymob function - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'User Name',
      'Governorate',
      'District',
      'Received Account',
      'Transaction ID',
      'Transaction Type',
      'Provider',
      'Amount',
      'Balance Before',
      'Admin Fees',
      'Provider Fees',
      'Net',
      'Status',
      'Created At',
      'MSISDN'
    ];

    return extractedData.map((transactionData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};

        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'User Type': transactionData?.userType ?? '_',
          'User ID': transactionData?.userId ?? '-',
          'User Name': transactionData?.userName ?? '_',
          'Governorate': transactionData?.governorate ?? '_',
          'District': transactionData?.district ?? '_',
          'Received Account': transactionData?.ReceivedAccount ?? transactionData?.receivedAccount ?? '_',
          'Transaction ID': transactionData?.transactionId ?? '-',
          'Transaction Type': transactionData?.transactionType ?? '_',
          'Provider': transactionData?.provider ?? '_',
          'Amount': transactionData?.amount ?? '_',
          'Balance Before': transactionData?.balanceBefore ?? '_',
          'Admin Fees': transactionData?.adminFees ?? '_',
          'Provider Fees': transactionData?.providerFees ?? '_',
          'Net': transactionData?.net ?? '_',
          'Status': transactionData?.status ?? '_',
          'Created At': transactionData?.createdAt ?? '_',
          'MSISDN': transactionData?.msisdn ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, transactionData, [
          'userType', 'userId', 'userName', 'governorate', 'district', 'ReceivedAccount',
          'receivedAccount', 'transactionId', 'transactionType', 'provider', 'amount',
          'balanceBefore', 'adminFees', 'providerFees', 'net', 'status', 'createdAt', 'msisdn'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting paymob data at index ${index}:`, error, transactionData);
        return {
          'Transaction ID': `Error_${index}`,
          'Error': 'Failed to format paymob data'
        };
      }
    });
  }, []);

  // Format Reset Logs data based on Apps Script function
  const formatResetLogsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatResetLogsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script transactionLog function - exact order
    const columnOrder = [
      'ID',
      'Warehouse Name',
      'User ID', 
      'User Name',
      'User Phone',
      'Action',
      'QR Code Type',
      'QR Code Value',
      'Product Type',
      'SKU Code',
      'Order QR',
      'Created Date',
      'Created Time'
    ];

    return data.reduce((acc, logData, index) => {
      try {
        // No action filtering here since API returns all data and Google Apps Script filters on display
        
        // Handle nested SKU codes structure (like Google Apps Script loop)
        const skuCodes = logData?.sKUCode || [];
        if (Array.isArray(skuCodes) && skuCodes.length > 0) {
          skuCodes.forEach((sku, skuIndex) => {
            const orderedObject: any = {};
            
            // Format dates similar to Apps Script
            let createdDate = '_';
            let createdTime = '_';
            
            if (logData?.createdAt) {
              try {
                const date = new Date(logData.createdAt);
                createdDate = date.toLocaleDateString();
                createdTime = date.toLocaleTimeString();
              } catch (dateError) {
                console.warn('Date formatting error:', dateError);
              }
            }
            
            // Map data to columns based on Apps Script structure
            const dataMapping: { [key: string]: any } = {
              'ID': logData?._id ?? '-',
              'Warehouse Name': logData?.warehouseName ?? '_',
              'User ID': logData?.userId ?? '-',
              'User Name': logData?.userName ?? '_',
              'User Phone': logData?.userPhone ?? '_',
              'Action': logData?.action ?? '_',
              'QR Code Type': logData?.QrcodeType ?? '_',
              'QR Code Value': logData?.QrcodeValue ?? '_',
              'Product Type': logData?.ProductType ?? '_',
              'SKU Code': sku?.code ?? '_',
              'Order QR': logData?.orderQr || 'Null',
              'Created Date': createdDate,
              'Created Time': createdTime
            };
            
            // Build object in the specified order
            columnOrder.forEach(column => {
              orderedObject[column] = dataMapping[column];
            });
            
            acc.push(orderedObject);
          });
        } else {
          // Handle case where there are no SKU codes
          const orderedObject: any = {};
          
          // Format dates
          let createdDate = '_';
          let createdTime = '_';
          
          if (logData?.createdAt) {
            try {
              const date = new Date(logData.createdAt);
              createdDate = date.toLocaleDateString();
              createdTime = date.toLocaleTimeString();
            } catch (dateError) {
              console.warn('Date formatting error:', dateError);
            }
          }
          
          const dataMapping: { [key: string]: any } = {
            'ID': logData?._id ?? '-',
            'Warehouse Name': logData?.warehouseName ?? '_',
            'User ID': logData?.userId ?? '-',
            'User Name': logData?.userName ?? '_',
            'User Phone': logData?.userPhone ?? '_',
            'Action': logData?.action ?? '_',
            'QR Code Type': logData?.QrcodeType ?? '_',
            'QR Code Value': logData?.QrcodeValue ?? '_',
            'Product Type': logData?.ProductType ?? '_',
            'SKU Code': '_',
            'Order QR': logData?.orderQr || 'Null',
            'Created Date': createdDate,
            'Created Time': createdTime
          };
          
          columnOrder.forEach(column => {
            orderedObject[column] = dataMapping[column];
          });
          
          acc.push(orderedObject);
        }
        
        return acc;
      } catch (error) {
        console.error(`Error formatting reset logs data at index ${index}:`, error, logData);
        return acc;
      }
    }, [] as any[]);
  }, [dynamicParams]);

  // Format Return System Scan View data based on the /return-system/scan-view API response
  const formatReturnSystemScanViewData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatReturnSystemScanViewData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the /return-system/scan-view API response - exact order
    const columnOrder = [
      'ID',
      'SKU ID',
      'SKU Name',
      'Product Type',
      'Batch Number',
      'Operation Batch',
      'Patch',
      'Order Number',
      'QR Value',
      'Warehouse User ID',
      'Warehouse User Name',
      'Warehouse Name',
      'Return Time Stamp',
      'Distributor IN ID',
      'Distributor IN Name',
      'Distributor IN Date',
      'Distributor OUT ID',
      'Distributor OUT Name',
      'Distributor OUT Date',
      'Retailer IN ID',
      'Retailer IN Name',
      'Retailer IN Date',
      'Retailer OUT ID',
      'Retailer OUT Name',
      'Retailer OUT Date',
      'Electrician ID',
      'Electrician Use Name',
      'Electrician Use Date'
    ];

    // Format GMT date strings from the API into the browser's local date/time
    const formatDate = (value: any): any => {
      if (!value) return '-';
      const dateObj = new Date(value);
      return isNaN(dateObj.getTime()) ? value : dateObj.toLocaleString();
    };

    return extractedData.map((scanData, index) => {
      try {
        const orderedObject: any = {};

        // Map data to columns based on the scan-view API structure
        const dataMapping: { [key: string]: any } = {
          'SKU ID': scanData?.['SKU ID'] || '-',
          'SKU Name': scanData?.['SKU Name'] || '-',
          'Product Type': scanData?.['Product Type'] || '-',
          'Batch Number': scanData?.['Batch Number'] || '-',
          'Operation Batch': scanData?.['Operation Batch'] || '-',
          'Patch': scanData?.['Patch'] || '-',
          'Order Number': scanData?.['Order Number'] || '-',
          'QR Value': scanData?.['QR Value'] || '-',
          'Warehouse Name': scanData?.['Warehouse Name'] || '-',
          'Warehouse User ID': scanData?.['Warehouse User ID'] || '-',
          'Warehouse User Name': scanData?.['Warehouse User Name'] || '-',
          'Distributor IN ID': scanData?.['Distributor IN ID'] || '-',
          'Distributor IN Name': scanData?.['Distributor IN Name'] || '-',
          'Distributor IN Date': formatDate(scanData?.['Distributor IN Date']),
          'Distributor OUT ID': scanData?.['Distributor OUT ID'] || '-',
          'Distributor OUT Name': scanData?.['Distributor OUT Name'] || '-',
          'Distributor OUT Date': formatDate(scanData?.['Distributor OUT Date']),
          'Retailer IN ID': scanData?.['Retailer IN ID'] || '-',
          'Retailer IN Name': scanData?.['Retailer IN Name'] || '-',
          'Retailer IN Date': formatDate(scanData?.['Retailer IN Date']),
          'Retailer OUT ID': scanData?.['Retailer OUT ID'] || '-',
          'Retailer OUT Name': scanData?.['Retailer OUT Name'] || '-',
          'Retailer OUT Date': formatDate(scanData?.['Retailer OUT Date']),
          'Electrician ID': scanData?.['Electrician ID'] || '-',
          'Electrician Use Name': scanData?.['Electrician Use Name'] || '-',
          'Electrician Use Date': formatDate(scanData?.['Electrician Use Date']),
          'Return Time Stamp': formatDate(scanData?.['Return Time Stamp']),
          'ID': scanData?.id ?? (index + 1)
        };

        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        const knownRawKeys = columnOrder.map(column => column === 'ID' ? 'id' : column);
        appendUnmappedFields(orderedObject, scanData, knownRawKeys);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting return system scan view data at index ${index}:`, error, scanData);
        return {
          'SKU ID': `Error_${index}`,
          'Error': 'Failed to format return system scan view data'
        };
      }
    });
  }, []);

  // Format Return System Incentive data based on Apps Script incentive function
  const formatReturnSystemIncentiveData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatReturnSystemIncentiveData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script incentive function - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'Product Type',
      'Products Name',
      'Products Code',
      'Quantity',
      'Purchase Points',
      'Sells Points',
      'Use Points',
      'Backward Points',
      'Backward Money',
      'Wheel Points',
      'Action',
      'Scan Created At',
      'Data Created At',
      'ID'
    ];
    
    // Process data similar to Apps Script - iterate through each data item and its scanArray
    const rows: any[] = [];
    
    data.forEach((incentiveData, dataIndex) => {
      if (incentiveData?.scanArray && Array.isArray(incentiveData.scanArray)) {
        incentiveData.scanArray.forEach((scan: any, scanIndex: number) => {
          try {
            const orderedObject: any = {};
            
            // Map data to columns based on Apps Script structure
            const dataMapping: { [key: string]: any } = {
              'User Type': scan?.userType ?? '_',
              'User ID': scan?.userId ?? '_',
              'Product Type': incentiveData?.productType ?? '_',
              'Products Name': incentiveData?.productsName ?? '_',
              'Products Code': incentiveData?.productsCode ?? '_',
              'Quantity': 1, // Fixed value as in Apps Script
              'Purchase Points': scan?.purchasePoints ?? 0,
              'Sells Points': scan?.sellsPoints ?? 0,
              'Use Points': scan?.usePoints ?? 0,
              'Backward Points': scan?.backwordPoints ?? '_',
              'Backward Money': scan?.backwordMoney ?? '_',
              'Wheel Points': scan?.wheelPoints ?? '_',
              'Action': scan?.action ?? '_',
              'Scan Created At': scan?.createdAt ?? '_',
              'Data Created At': incentiveData?.createdAt ?? '_',
              'ID': incentiveData?._id ?? '_'
            };
            
            // Build object in the specified order
            columnOrder.forEach(column => {
              orderedObject[column] = dataMapping[column];
            });
            
            rows.push(orderedObject);
          } catch (error) {
            console.error(`Error formatting return system incentive data at data index ${dataIndex}, scan index ${scanIndex}:`, error, scan);
            rows.push({
              'ID': `Error_${dataIndex}_${scanIndex}`,
              'Error': 'Failed to format return system incentive data'
            });
          }
        });
      }
    });
    
    return rows;
  }, []);

  // Format Gifts data based on Apps Script function
  const formatGiftsData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatGiftsData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the /client-request/data/gift/sheet API response - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'User Name',
      'Phone',
      'Governorate',
      'District',
      'ID',
      'Gift Name',
      'Gift Value',
      'Gift Status',
      'Gift Milestone',
      'Gift Milestone Count',
      'Date'
    ];

    return extractedData.map((giftData, index) => {
      try {
        // Create object in the exact order defined above
        const orderedObject: any = {};

        // API already returns human-readable keys; just re-map into the desired order
        const dataMapping: { [key: string]: any } = {
          'User Type': giftData?.['User Type'] ?? '_',
          'User ID': giftData?.['User ID'] ?? '-',
          'User Name': giftData?.['User Name'] ?? '_',
          'Phone': giftData?.['Phone'] ?? '_',
          'Governorate': giftData?.['Governorate'] ?? '_',
          'District': giftData?.['District'] ?? '_',
          'ID': giftData?.['_id'] ?? '-',
          'Gift Name': giftData?.['Gift Name'] ?? '_',
          'Gift Value': giftData?.['Gift Value'] ?? '_',
          'Gift Status': giftData?.['Gift Status'] ?? '_',
          'Gift Milestone': giftData?.['Gift Milestone'] ?? '_',
          'Gift Milestone Count': giftData?.['Gift Milestone Count'] ?? '_',
          'Date': giftData?.['Date'] ?? '_'
        };

        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        const knownRawKeys = columnOrder.map(column => column === 'ID' ? '_id' : column);
        appendUnmappedFields(orderedObject, giftData, knownRawKeys);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting gift data at index ${index}:`, error, giftData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format gift data'
        };
      }
    });
  }, []);

  // Format End Of Month data based on the /users/data/sheet/endOfMonth API response
  const formatEndOfMonthData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatEndOfMonthData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the endOfMonth API response - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'User Name',
      'Shop Name',
      'Cash Value',
      'Factor',
      'Points Earned',
      'Month',
      'Year'
    ];

    return extractedData.map((monthData, index) => {
      try {
        const orderedObject: any = {};

        const dataMapping: { [key: string]: any } = {
          'User Type': monthData?.user_type ?? '_',
          'User ID': monthData?.user_id ?? '-',
          'User Name': monthData?.user_name ?? '_',
          'Shop Name': monthData?.shop_name ?? '_',
          'Cash Value': monthData?.cash_value ?? '_',
          'Factor': monthData?.factor ?? '_',
          'Points Earned': monthData?.points_earned ?? '_',
          'Month': monthData?.month ?? '_',
          'Year': monthData?.year ?? '_'
        };

        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, monthData, [
          'user_type', 'user_id', 'user_name', 'shop_name', 'cash_value', 'factor', 'points_earned', 'month', 'year'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting end of month data at index ${index}:`, error, monthData);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format end of month data'
        };
      }
    });
  }, []);

  // Format Invoice Photos data based on Apps Script function
  const formatInvoicePhotosData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatInvoicePhotosData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script invoices function - exact order
    const columnOrder = [
      'ID',
      'User ID',
      'Full Name',
      'Phone',
      'User Type',
      'Governorate',
      'District',
      'Images',
      'Created At'
    ];
    
    return data.map((invoiceData, index) => {
      try {
        const orderedObject: any = {};
        
        // Special logic for User Type: convert "Electrician" to "EndUser"
        let userType = invoiceData?.userData?.userType?.name ?? '_';
        if (userType === 'Electrician') {
          userType = 'EndUser';
        }
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': invoiceData?._id ?? '_',
          'User ID': invoiceData?.userData?.userId ?? '-',
          'Full Name': invoiceData?.userData?.fullName ?? '-',
          'Phone': invoiceData?.userData?.phone ?? '_',
          'User Type': userType,
          'Governorate': invoiceData?.userData?.governate?.name ?? '_',
          'District': invoiceData?.userData?.dirstrict?.name ?? '_',
          'Images': invoiceData?.images ?? '_',
          'Created At': invoiceData?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting invoice photos data at index ${index}:`, error, invoiceData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format invoice photos data'
        };
      }
    });
  }, []);

  // Format Joining Requests data based on Apps Script function
  const formatJoiningRequestsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatJoiningRequestsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script JoiningRequests function - exact order
    const columnOrder = [
      'ID',
      'First Name',
      'Last Name',
      'Phone',
      'District',
      'Governorate',
      'Date',
      'Time',
      'Status'
    ];
    
    return data.map((requestData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': requestData?._id ?? '-',
          'Date': requestData?.date ?? '-',
          'Time': requestData?.time ?? '_',
          'First Name': requestData?.firstName ?? '_',
          'Last Name': requestData?.lastName ?? '_',
          'Phone': requestData?.phone ?? '_',
          'Governorate': requestData?.governorate ?? '_',
          'District': requestData?.district ?? '_',
          'Status': requestData?.status ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, requestData, [
          '_id', 'date', 'time', 'firstName', 'lastName', 'phone', 'governorate', 'district', 'status'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting joining request data at index ${index}:`, error, requestData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format joining request data'
        };
      }
    });
  }, []);

  // Format National IDs data
  // Backend (/reports/id_verifications) returns { docs: [...] } with display-ready keys.
  const formatNationalIdsData = useCallback((data: any): any[] => {
    const rows = Array.isArray(data) ? data : data?.docs;
    if (!Array.isArray(rows)) {
      console.warn('formatNationalIdsData: Expected array or { docs }, got:', typeof data);
      return [];
    }

    const columnOrder = [
      'User Name',
      'User ID',
      'User Type',
      'Mobile Number',
      'Address',
      'National ID Number',
      'ID Image Front',
      'ID Image Back',
      'Expiry Date',
      'Birth Date',
      'Date & Time Stamp',
      'Transaction Status',
      'Failure Type'
    ];

    return rows.map((idData, index) => {
      try {
        const orderedObject: any = {};

        columnOrder.forEach(column => {
          let value = idData?.[column];
          // Expiry/Birth dates: keep the date part only, drop any time
          if ((column === 'Expiry Date' || column === 'Birth Date') && typeof value === 'string') {
            const text = value.trim();
            const iso = text.match(/^\d{4}-\d{2}-\d{2}/);
            // JS Date string, e.g. "Wed Sep 30 2026 00:00:00 GMT+0000"
            const jsDate = text.match(/^[A-Za-z]{3},?\s+([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/);
            // UTC string, e.g. "Wed, 30 Sep 2026 00:00:00 GMT"
            const utcDate = text.match(/^[A-Za-z]{3},?\s+(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
            // Output: YYYY-MM-DD (same as the database format)
            const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const toCardDate = (y: string, m: number, d: string) =>
              `${y}-${String(m).padStart(2, '0')}-${d.padStart(2, '0')}`;
            const utcMonth = utcDate ? months.indexOf(utcDate[2].toLowerCase()) + 1 : 0;
            const jsMonth = jsDate ? months.indexOf(jsDate[1].toLowerCase()) + 1 : 0;
            if (iso) {
              value = iso[0];
            } else if (utcDate && utcMonth) {
              value = toCardDate(utcDate[3], utcMonth, utcDate[1]);
            } else if (jsDate && jsMonth) {
              value = toCardDate(jsDate[3], jsMonth, jsDate[2]);
            } else {
              value = text.split(/T|,\s|\s\d{1,2}:\d{2}/)[0];
            }
          }
          // Show NEEDS_REVIEW transactions as REJECTED
          if (column === 'Transaction Status' && typeof value === 'string' && value.trim().toUpperCase() === 'NEEDS_REVIEW') {
            value = 'REJECTED';
          }
          orderedObject[column] = value === null || value === undefined || value === '' ? '_' : value;
        });

        // Surface any extra fields the backend adds later (e.g. Religion, Marital Status)
        appendUnmappedFields(orderedObject, idData, columnOrder);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting national ID data at index ${index}:`, error, idData);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format national ID data'
        };
      }
    });
  }, []);

  // Format Deposit Amount Log data based on Apps Script function
  const formatDepositAmountLogData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatDepositAmountLogData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script depositAmountLog function - exact order
    const columnOrder = [
      'ID',
      'User ID',
      'User Name',
      'Transaction Type',
      'Value',
      'Previous Balance',
      'Current Balance',
      'Comment',
      'Created By',
      'Created At'
    ];
    
    return data.map((transactionData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns based on Apps Script structure
        const dataMapping: { [key: string]: any } = {
          'ID': transactionData?._id ?? '-',
          'User ID': transactionData?.userId ?? '_',
          'User Name': transactionData?.userName ?? '_',
          'Transaction Type': transactionData?.transactionType ?? '_',
          'Value': transactionData?.value ?? '_',
          'Previous Balance': transactionData?.previousBalance ?? '_',
          'Current Balance': transactionData?.currentBalance ?? '_',
          'Comment': transactionData?.comment ?? '_',
          'Created By': transactionData?.createdBy ?? '_',
          'Created At': transactionData?.createdAt ?? '_'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });
        
        return orderedObject;
      } catch (error) {
        console.error(`Error formatting deposit amount log data at index ${index}:`, error, transactionData);
        return {
          'ID': `Error_${index}`,
          'Error': 'Failed to format deposit amount log data'
        };
      }
    });
  }, []);

  // Format Deposit Amount data based on Apps Script function with validation and POST functionality
  const formatDepositAmountData = (inputData: any): any => {
    // This function handles both validation and POST request for deposit amount
    const validateAndSubmitDeposit = async () => {
      try {
        // Use the depositTransactions state instead of dynamicParams
        const selectedData = [];
        
        // Double-check validation (safeguard)
        if (depositTransactions.length === 0) {
          throw new Error('Please add at least one transaction before sending the operation.');
        }
        
        // Process each transaction (validation already done in executeApi)
        for (let i = 0; i < depositTransactions.length; i++) {
          const tx = depositTransactions[i];
          
          const numericValue = Number(tx.value);
          // Get login email from localStorage - try multiple possible keys
          const createdBy = localStorage.getItem('user_email') || 
                           localStorage.getItem('email') || 
                           localStorage.getItem('api_username') || 
                           localStorage.getItem('google_email') ||
                           localStorage.getItem('userEmail') ||
                           'qara-sheets';
          const transactionData = {
            userId: tx.userId.toString(),
            transactionType: tx.transactionType,
            value: numericValue,
            comment: tx.comment || '',
            createdBy: createdBy
          };
          selectedData.push(transactionData);
        }
        
        // Confirmation dialog simulation
        const confirmed = window.confirm('Are you sure to make this operation?');
        if (!confirmed) {
          return { message: 'Operation cancelled by user' };
        }
        
        // Prepare API data
        const apiData = { transactions: selectedData };
        
        // Make POST request
        const response = await apiService.submitDepositAmount(apiData);
        
        if (response?.status === 201) {
          // Extract message from response body - handle different response structures
          let successMessage = 'Deposit amount processed successfully';
          
          if (response.data) {
            // Try multiple possible message fields in the response
            successMessage = response.data.body?.message || 
                           successMessage;
          }
          
          alert(successMessage);
          
          // Clear transactions after successful submission
          setDepositTransactions([]);
          
          return response.data;
        } else {
          throw new Error(response?.data?.description || 'Failed to process deposit amount');
        }
        
      } catch (error: any) {
        console.error('Deposit Amount Error:', error);
        
        // Extract error message from API response - handle different error structures
        let errorMessage = 'Failed to process deposit amount';
        
        if (error.response?.data) {
          // Try multiple possible error message fields in the response
          errorMessage = error.response.data.body?.message || 
                       error.response.data.description || 
                       error.response.data.error ||
                       errorMessage;
        } else if (error.message) {
          // Parse error message if it contains JSON
          if (error.message.includes('{')) {
            try {
              const jsonStart = error.message.indexOf('{');
              const jsonEnd = error.message.lastIndexOf('}');
              if (jsonStart !== -1 && jsonEnd !== -1) {
                const jsonStr = error.message.substring(jsonStart, jsonEnd + 1);
                const parsedError = JSON.parse(jsonStr);
                errorMessage = parsedError.description || parsedError.message || error.message;
              }
            } catch (parseError) {
              errorMessage = error.message;
            }
          } else {
            errorMessage = error.message;
          }
        }
        
        alert('Error: ' + errorMessage);
        throw error;
      }
    };
    
    // Return the validation and submission function
    return validateAndSubmitDeposit();
  };

  // Format Welcome Bonus data based on the /wallet-transactions/WelcomeBonusForSheet API response
  const formatWelcomeBonusData = useCallback((data: any): any[] => {
    // Extract docs array from the paginated response structure
    let extractedData = data;
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.docs)) {
      extractedData = data.docs;
    }

    if (!Array.isArray(extractedData)) {
      console.warn('formatWelcomeBonusData: Expected array, got:', typeof extractedData);
      return [];
    }

    // Define columns based on the WelcomeBonusForSheet API response - exact order
    const columnOrder = [
      'User Type',
      'User ID',
      'First Name',
      'Last Name',
      'Phone',
      'Governorate',
      'District',
      'Value',
      'Created At'
    ];

    return extractedData.map((bonusData, index) => {
      try {
        const orderedObject: any = {};

        const dataMapping: { [key: string]: any } = {
          'User Type': bonusData?.user_type_name ?? '_',
          'User ID': bonusData?.user_id ?? '-',
          'First Name': bonusData?.first_name ?? '_',
          'Last Name': bonusData?.last_name ?? '_',
          'Phone': bonusData?.phone ?? '_',
          'Governorate': bonusData?.governorate_name ?? '_',
          'District': bonusData?.district_name ?? '_',
          'Value': bonusData?.value ?? '_',
          'Created At': bonusData?.created_at ?? '_'
        };

        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, bonusData, [
          'user_type_name', 'user_id', 'first_name', 'last_name', 'phone',
          'governorate_name', 'district_name', 'value', 'created_at'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting welcome bonus data at index ${index}:`, error, bonusData);
        return {
          'User ID': `Error_${index}`,
          'Error': 'Failed to format welcome bonus data'
        };
      }
    });
  }, []);

  // Format Wallet Transactions data based on Apps Script function
  const formatWalletTransactionsData = useCallback((data: any[]): any[] => {
    if (!Array.isArray(data)) {
      console.warn('formatWalletTransactionsData: Expected array, got:', typeof data);
      return [];
    }
    
    // Define columns based on the Apps Script walletTransaction function
    const columnOrder = [
      'Index',
      'User Type',
      'User ID',
      'Full Name',
      'Transaction Type',
      'Value',
      'Balance Before',
      'Balance After',
      'Created At'
    ];
    
    return data.map((transactionData, index) => {
      try {
        const orderedObject: any = {};
        
        // Map data to columns - handle both camelCase and snake_case properties from API
        const dataMapping: { [key: string]: any } = {
          'Index': (index + 1),
          'Transaction Type': transactionData?.transaction_type ?? transactionData?.transactionType ?? '-',
          'Value': transactionData?.value ?? '_',
          'Balance Before': transactionData?.balance_before ?? transactionData?.balanceBefore ?? '_',
          'Balance After': transactionData?.balance_after ?? transactionData?.balanceAfter ?? '_',
          'Created At': transactionData?.created_at ?? transactionData?.createdAt ?? '_',
          'User ID': transactionData?.user_id ?? transactionData?.userId ?? '_',
          'Full Name': `${transactionData?.first_name ?? transactionData?.firstName ?? ''} ${transactionData?.last_name ?? transactionData?.lastName ?? ''}`.trim() || '-',
          'User Type': transactionData?.user_type_name ?? transactionData?.userType ?? transactionData?.userTypeName ?? '-'
        };
        
        // Build object in the specified order
        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, transactionData, [
          'transaction_type', 'transactionType', 'value', 'balance_before', 'balanceBefore',
          'balance_after', 'balanceAfter', 'created_at', 'createdAt', 'user_id', 'userId',
          'first_name', 'firstName', 'last_name', 'lastName', 'user_type_name', 'userType', 'userTypeName'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting wallet transactions data at index ${index}:`, error, transactionData);
        return {
          'Index': `Error_${index}`,
          'Error': 'Failed to format wallet transactions data'
        };
      }
    });
  }, []);

  // Format User Details Per Product Per Action data and other QR codes endpoints
  const formatUserDetailsPerProductPerActionData = useCallback((data: any): any[] => {
    
    // Handle nested API response structure like the Apps Script
    let extractedData = data;
    
    // If data is an object, extract the docs array from the nested structure
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      // Look for docs in the first key of the object (like Apps Script does)
      const keys = Object.keys(data);
      if (keys.length > 0) {
        const firstKey = keys[0];
        if (data[firstKey] && data[firstKey].docs && Array.isArray(data[firstKey].docs)) {
          extractedData = data[firstKey].docs;
        } else if (Array.isArray(data[firstKey])) {
          // Sometimes the data might be directly in the first key without 'docs'
          extractedData = data[firstKey];
        }
      }
    }
    
    if (!Array.isArray(extractedData)) {
      console.warn('Expected array for QR codes data, received:', typeof extractedData);
      return [];
    }
    
    // Define columns based on the Apps Script userScanAnalysis function
    const columnOrder = [
      'User Type Name',
      'User ID',
      'User Name',
      'Phone',
      'Shop Name',
      'Governorate',
      'District',
      'Counter',
      'Scan Action',
      'Created At',
      'Product Name',
      'Product Code',
      'Product Type Name',
      'Wallet Type',
      'Purchase Cash',
      'Sells Cash',
      'Use Cash',
      'Purchase Points',
      'Sells Points',
      'Use Points',
      'Backward Points',
      'Backward Money',
      'Wheel Points'
    ];

    return extractedData.map((scanData, index) => {
      try {
        // If the data doesn't have the expected structure for User Details Per Product Per Action,
        // return the data as-is with basic formatting
        if (!scanData.hasOwnProperty('userTypeName') && !scanData.hasOwnProperty('productName')) {
          return scanData; // Return raw data for other QR codes endpoints
        }

        const orderedObject: any = {};

        const dataMapping: { [key: string]: any } = {
          'User Type Name': scanData?.userTypeName ?? '-',
          'User ID': scanData?.userId ?? '_',
          'User Name': scanData?.userName ?? '_',
          'Phone': scanData?.phone ?? '_',
          'Shop Name': scanData?.shopName ?? '_',
          'Governorate': scanData?.governorate ?? '_',
          'District': scanData?.district ?? '_',
          'Counter': scanData?.counter ?? '_',
          'Scan Action': scanData?.scanAction ?? '_',
          'Created At': scanData?.createdAt ?? '_',
          'Product Name': scanData?.productName ?? '-',
          'Product Code': scanData?.productCode ?? '-',
          'Product Type Name': scanData?.productTypeName ?? '_',
          'Wallet Type': scanData?.WalletType ?? "_",
          'Purchase Cash': scanData?.purchaseCash ?? "_",
          'Sells Cash': scanData?.sellsCash ?? "_",
          'Use Cash': scanData?.useCash ?? "_",
          'Purchase Points': scanData?.purchasePoints ?? '_',
          'Sells Points': scanData?.sellsPoints ?? '_',
          'Use Points': scanData?.usePoints ?? '_',
          'Backward Points': scanData?.backwordPoints ?? '_',
          'Backward Money': scanData?.backwordMoney ?? '_',
          'Wheel Points': scanData?.wheelPoints ?? '_'
        };

        columnOrder.forEach(column => {
          orderedObject[column] = dataMapping[column];
        });

        // Automatically surface any new fields the backend adds later, at the end
        appendUnmappedFields(orderedObject, scanData, [
          'userTypeName', 'userId', 'userName', 'phone', 'shopName', 'governorate', 'district',
          'counter', 'scanAction', 'createdAt', 'productName', 'productCode', 'productTypeName',
          'WalletType', 'purchaseCash', 'sellsCash', 'useCash', 'purchasePoints', 'sellsPoints',
          'usePoints', 'backwordPoints', 'backwordMoney', 'wheelPoints'
        ]);

        return orderedObject;
      } catch (error) {
        console.error(`Error formatting QR codes data at index ${index}:`, error, scanData);
        return scanData; // Return raw data if formatting fails
      }
    });
  }, []);

  const renderTableView = (data: any): JSX.Element | null => {
    if (!data) return null;
    
    try {
      // Format data based on endpoint type
      let processedData = data;
      if (selectedEndpoint?.endpoint === 'Users Types') {
        processedData = formatUsersTypesData(data);
      } else if (selectedEndpoint?.endpoint === 'Users Data') {
        processedData = formatMasterDataData(data);
      } else if (selectedEndpoint?.endpoint === 'Welcome Bonus') {
        processedData = formatWelcomeBonusData(data);
      } else if (selectedEndpoint?.endpoint === 'Users Points' && Array.isArray(data)) {
        processedData = formatUsersPointsData(data);
      } else if (selectedEndpoint?.endpoint === 'Referral Users' && Array.isArray(data)) {
        processedData = formatReferralUsersData(data);
      } else if (selectedEndpoint?.endpoint === 'Referred Users' && Array.isArray(data)) {
        processedData = formatReferredUsersData(data);
      } else if (selectedEndpoint?.endpoint === 'User Details Per Product Per Action') {
        processedData = formatUserDetailsPerProductPerActionData(data);
      } else if (selectedEndpoint?.endpoint === 'Configuration Result Per Product') {
        // Extract docs array if response is nested, otherwise use data directly
        let configData = data;
        if (data && typeof data === 'object' && !Array.isArray(data) && data.docs && Array.isArray(data.docs)) {
          configData = data.docs;
        }
        if (Array.isArray(configData)) {
          processedData = formatConfigurationResultPerProductData(configData);
        }
      } else if (selectedEndpoint?.endpoint === 'Deactivated Incentive QR' && Array.isArray(data)) {
        processedData = formatDeactivatedIncentiveQRData(data);
      } else if (selectedEndpoint?.endpoint === 'Order Scanout From Warehouse' && Array.isArray(data)) {
        processedData = formatOrderScanoutFromWarehouseData(data);
      } else if (selectedEndpoint?.endpoint === 'Scans per product per user type' && Array.isArray(data)) {
        processedData = formatScansPerProductPerUserTypeData(data);
      } else if (selectedEndpoint?.endpoint === 'Scan Per Product details' && (Array.isArray(data) || (data && data.docs && Array.isArray(data.docs)))) {
        processedData = formatScanPerProductDetailsData(data);
      } else if (selectedEndpoint?.endpoint === 'Gifts' && (Array.isArray(data) || (data && Array.isArray(data.docs)))) {
        processedData = formatGiftsData(data);
      } else if (selectedEndpoint?.endpoint === 'End Of Month' && (Array.isArray(data) || (data && Array.isArray(data.docs)))) {
        processedData = formatEndOfMonthData(data);
      } else if (selectedEndpoint?.endpoint === 'Warehouse Scan In/Out' && (Array.isArray(data) || (data && Array.isArray(data.docs)))) {
        processedData = formatWarehouseScanInOutData(data);
      } else if (selectedEndpoint?.endpoint === 'Warehouse Quantity Aggregation' && Array.isArray(data)) {
        processedData = formatWarehouseQuantityAggregationData(data);
      } else if (selectedEndpoint?.endpoint === 'Wallet Transactions') {
        // Handle nested data structure - API returns data in various formats
        let walletData = data;
        
        console.log('Wallet Transactions - Raw response:', { data, type: typeof data, isArray: Array.isArray(data), keys: typeof data === 'object' ? Object.keys(data) : 'N/A' });
        
        // Try different response structures
        if (data && typeof data === 'object') {
          // Check for common nested structures
          if (data.docs && Array.isArray(data.docs)) {
            walletData = data.docs;
            console.log('Wallet Transactions - Found in .docs');
          }
          // Check for body array
          else if (data.body && Array.isArray(data.body)) {
            walletData = data.body;
            console.log('Wallet Transactions - Found in .body');
          }
          // Check for data property with array
          else if (data.data && Array.isArray(data.data)) {
            walletData = data.data;
            console.log('Wallet Transactions - Found in .data');
          }
          // Check for result/results
          else if (data.result && Array.isArray(data.result)) {
            walletData = data.result;
            console.log('Wallet Transactions - Found in .result');
          }
          // Look for any array-like property that might contain transaction data
          else if (!Array.isArray(data)) {
            // Search through all properties to find an array
            const arrayProps = Object.entries(data).filter(([key, value]) => Array.isArray(value) && (value as any[]).length > 0);
            if (arrayProps.length > 0) {
              const [propName, propValue] = arrayProps[0];
              walletData = propValue as any[];
              console.log('Wallet Transactions - Found array in property:', propName);
            }
            else if (Array.isArray(data)) {
              walletData = data;
            }
            else {
              console.warn('Wallet Transactions: Could not find array in response structure', { data, keys: Object.keys(data) });
              walletData = [];
            }
          } else if (Array.isArray(data)) {
            walletData = data;
          }
        } else if (Array.isArray(data)) {
          walletData = data;
        } else {
          console.warn('Wallet Transactions: Unexpected data type', typeof data);
          walletData = [];
        }
        
        console.log('Wallet Transactions - Extracted data:', { isArray: Array.isArray(walletData), length: Array.isArray(walletData) ? walletData.length : 0 });
        
        if (Array.isArray(walletData) && walletData.length > 0) {
          processedData = formatWalletTransactionsData(walletData);
        } else {
          processedData = [];
        }
      } else if (selectedEndpoint?.endpoint === 'Deposit Amount') {
        // Handle Deposit Amount as success message display
        if (data && (data.message || data.data)) {
          processedData = [{
            'Operation': 'Deposit Amount',
            'Status': 'Success',
            'Message': data.message || data.data?.message || 'Operation completed successfully',
            'Timestamp': new Date().toLocaleString()
          }];
        } else {
          processedData = data;
        }
      } else if (selectedEndpoint?.endpoint === 'Paymob') {
        // Handle Paymob data formatting
        processedData = formatPaymobData(data);
      } else if (selectedEndpoint?.endpoint === 'invoice-photos' && Array.isArray(data)) {
        processedData = formatInvoicePhotosData(data);
      } else if (selectedEndpoint?.endpoint === 'joining-requests' && Array.isArray(data)) {
        processedData = formatJoiningRequestsData(data);
      } else if (selectedEndpoint?.endpoint === 'National IDs' && (Array.isArray(data) || Array.isArray(data?.docs))) {
        processedData = formatNationalIdsData(data);
      } else if (selectedEndpoint?.endpoint === 'reset-logs' && Array.isArray(data)) {
        processedData = formatResetLogsData(data);
      } else if (selectedEndpoint?.endpoint === 'Deposit Amount Log' && Array.isArray(data)) {
        processedData = formatDepositAmountLogData(data);
      } else if (selectedEndpoint?.endpoint === 'Return System' && (Array.isArray(data) || (data && Array.isArray(data.docs)))) {
        processedData = formatReturnSystemScanViewData(data);
      } else if (selectedEndpoint?.endpoint === 'Incentive' && Array.isArray(data)) {
        processedData = formatReturnSystemIncentiveData(data);
      } else if (selectedEndpoint?.category === 'QR Codes') {
        // Generic handling for other QR codes endpoints
        processedData = formatUserDetailsPerProductPerActionData(data);
      }
      
      
      // Ensure we have valid data to render OR we're still loading
      if (!loading && (!processedData || (Array.isArray(processedData) && processedData.length === 0))) {
        return (
          <div style={{
            padding: '20px',
            textAlign: 'center',
            color: '#666666'
          }}>
            No data available to display
          </div>
        );
      }
      
      // Use the same TableViewer component that's used in the new window
      // This ensures consistent data processing and rendering
      return (
        <div style={{
          width: '100%',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          background: 'white'
        }}>
          <TableViewer
            data={processedData}
            endpoint={selectedEndpoint?.endpoint || 'Unknown'}
            reportTitle={selectedEndpoint?.displayName}
            timestamp={new Date().toISOString()}
            showCloseButton={false}
            loading={loading}
            onOpenInNewWindow={openTableInNewWindow}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering table view:', error);
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          color: '#000000',
          border: '1px solid #cccccc',
          borderRadius: '4px',
          backgroundColor: '#f5f5f5'
        }}>
          Error rendering table data. Please check the console for details.
        </div>
      );
    }
  };
  


  useEffect(() => {
    loadEndpoints();
    loadUserTypes();
    
    // Cleanup function to cancel any pending requests when component unmounts
    return () => {
      if (currentRequestController) {
        currentRequestController.abort();
      }
    };
  }, []);
  
  // Cleanup request on component unmount or when currentRequestController changes
  useEffect(() => {
    return () => {
      if (currentRequestController) {
        currentRequestController.abort();
      }
    };
  }, [currentRequestController]);

  const loadEndpoints = async () => {
    try {
      const result = await apiService.getEndpoints();
      setEndpoints(result.data);
    } catch (error) {
      // Silently handle endpoint loading error
    } finally {
      setEndpointsLoaded(true);
    }
  };

  // Auto-load report from URL query parameter
  useEffect(() => {
    const reportParam = searchParams.get('report');
    if (reportParam && endpoints && Object.keys(endpoints).length > 0) {
      // Find the endpoint that matches the report name
      let foundEndpoint: ApiEndpoint | null = null;
      
      Object.values(endpoints).forEach((categoryEndpoints) => {
        const endpoint = categoryEndpoints.find(
          (ep) => ep.endpoint === reportParam || ep.endpoint === decodeURIComponent(reportParam)
        );
        if (endpoint && !foundEndpoint) {
          foundEndpoint = endpoint;
        }
      });
      
      if (foundEndpoint) {
        handleEndpointSelection(foundEndpoint);
      }
    }
  }, [searchParams, endpoints]);

  const loadUserTypes = async () => {
    try {
      const result = await apiService.getUserTypes();
      setUserTypes(result.data as string[]);

    } catch (error) {
      // Fallback to default user types
      setUserTypes(['Admin', 'user']);
    }
  };

  const executeApi = async (opts?: { userTriggered?: boolean }) => {
    if (!selectedEndpoint) return;
    const userTriggered = opts && opts.userTriggered === true;

    // Special validation for Deposit Amount endpoint
    if (selectedEndpoint.endpoint === 'Deposit Amount') {
      
      if (depositTransactions.length === 0) {
        alert('Please add at least one transaction before sending the operation.');
        return;
      }
      
      // Validate each transaction
      const invalidTransactions: string[] = [];
      depositTransactions.forEach((tx, index) => {
        if (!tx.userId || !tx.transactionType || !tx.value) {
          invalidTransactions.push(`Transaction ${index + 1}: Missing required fields`);
        } else if (Number(tx.value) <= 0 || isNaN(Number(tx.value))) {
          invalidTransactions.push(`Transaction ${index + 1}: Invalid amount (must be positive number)`);
        }
      });
      
      if (invalidTransactions.length > 0) {
        alert(`Please fix the following issues:\n\n${invalidTransactions.join('\n')}`);
        return;
      }
    }

    // Validate required parameters before making the API call
    const endpointPath = selectedEndpoint.endpoint;
    const paramConfig = getParameterConfig(endpointPath);

    // Compute current month defaults (used when filter fields are left empty)
    const _now = new Date();
    const currentMonthNum = _now.getMonth() + 1; // 1-indexed
    const currentMonthYear = _now.getFullYear();
    const currentMonthLastDay = new Date(currentMonthYear, currentMonthNum, 0).getDate();
    const currentMonthPad = String(currentMonthNum).padStart(2, '0');
    const defaultStartDate = `${currentMonthYear}-${currentMonthPad}-01`;
    const defaultEndDate = `${currentMonthYear}-${currentMonthPad}-${String(currentMonthLastDay).padStart(2, '0')}`;

    // Build effectiveParams:
    // - If dynamicParams has values (user typed filters), use them.
    // - Otherwise, if we have previously entered params for this endpoint
    //   (e.g. after "Clear Data" + "Load Data"), restore them so the report
    //   reloads with the same filters the user entered before.
    // - Otherwise, fall back to current-month defaults for empty date fields.
    const hasUserEnteredParams = Object.keys(dynamicParams).length > 0
      && Object.values(dynamicParams).some(v => v !== '' && v !== false);
    const preservedParams = lastEnteredParams[endpointPath] || {};

    const effectiveParams: { [key: string]: string | boolean } = hasUserEnteredParams
      ? { ...dynamicParams }
      : { ...preservedParams };

    paramConfig.forEach(param => {
      if (param.type === 'monthYear') {
        if (!effectiveParams['month'] || (effectiveParams['month'] as string).trim() === '') {
          effectiveParams['month'] = String(currentMonthNum);
          effectiveParams['year'] = String(currentMonthYear);
        }
      } else if (param.type === 'date' && param.name === 'startDate') {
        if (!effectiveParams['startDate'] || (effectiveParams['startDate'] as string).trim() === '') {
          effectiveParams['startDate'] = defaultStartDate;
        }
      } else if (param.type === 'date' && param.name === 'endDate') {
        if (!effectiveParams['endDate'] || (effectiveParams['endDate'] as string).trim() === '') {
          effectiveParams['endDate'] = defaultEndDate;
        }
      } else if (param.name === 'transactionType') {
        if (!effectiveParams['transactionType'] || (effectiveParams['transactionType'] as string).trim() === '') {
          effectiveParams['transactionType'] = 'All';
        }
      } else if (endpointPath === 'Users Types' && param.name === 'name') {
        if (!effectiveParams['name'] || (effectiveParams['name'] as string).trim() === '') {
          effectiveParams['name'] = 'Admin';
        }
      } else if ((endpointPath === 'Referral Users' || endpointPath === 'Referred Users') && param.name === 'dateType') {
        if (!effectiveParams['dateType'] || (effectiveParams['dateType'] as string).trim() === '') {
          effectiveParams['dateType'] = 'referDate';
        }
      }
    });

    // Reflect the params actually being used (including any defaults just
    // filled in above) back into the filter UI, so the date range picker
    // shows the range that produced the currently displayed data.
    if (!hasUserEnteredParams) {
      setDynamicParams(effectiveParams);
    }

    // Check for required parameters
    const missingRequiredParams: string[] = [];
    
    if (paramConfig && paramConfig.length > 0) {
      paramConfig.forEach(param => {
        if (param.required) {
          const value = effectiveParams[param.name];
          if (!value || (typeof value === 'string' && value.trim() === '') || value === 'loading') {
            missingRequiredParams.push(param.label);
          }
        }
      });
    }
    
    // Show error if required parameters are missing
    if (missingRequiredParams.length > 0) {
      setError(`Please choose the following required parameters: ${missingRequiredParams.join(', ')}`);
      return;
    }

    // Cancel any existing request
    if (currentRequestController) {
      currentRequestController.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    setCurrentRequestController(abortController);

    setLoading(true);
    setError('');
    setResponse(null);
    setLoadingProgress('');

    try {
      // Map the dashboard endpoint to the actual service method
      let result: { data: any };
      
      if (endpointPath === 'Users Types') {
        // Use dynamic params from form inputs for Users Types
        const parsedParams = JSON.parse(convertDynamicParamsToJson(effectiveParams));
        result = await apiService.getUsersExcel(parsedParams);
      } else if (endpointPath === 'Users Points') {
        if (userTriggered) {
          // When user explicitly clicks Load Data, request a single non-paginated response
          // so the backend returns the full dataset in one request (if supported).
          setLoadingProgress('Loading full report...');
          try {
            result = await apiService.getFeeds({ allowPagination: false });
          } finally {
            setLoadingProgress('');
          }
        } else {
          // Auto-load / quick preview - load last 100 records
          setLoadingProgress('Loading Report...');
          try {
            result = await apiService.getFeeds({ page: 1, limit: 100, allowPagination: true });
          } finally {
            setLoadingProgress('');
          }
        }
      } else if (endpointPath === 'Users') {
         if (userTriggered) {
           // User-requested full load - do not force small pagination
           result = await apiService.getUsersWithAcceptedBy({});
         } else {
           // Quick preview
           result = await apiService.getUsersWithAcceptedBy({ page: 1, limit: 100, allowPagination: true });
         }

      } else if (endpointPath === 'Users Data') {
         if (userTriggered) {
           // User-requested full load - do not force small pagination
           result = await apiService.getUsersData({});
         } else {
           // Quick preview
           result = await apiService.getUsersData({ page: 1, limit: 100, allowPagination: true });
         }

      } else if (endpointPath === 'Referral Users') {
        // Use dynamic params from form inputs for Referral Users
        const paramsJson = convertDynamicParamsToJson(effectiveParams);
        console.log('DEBUG: Referral Users - dynamicParams before JSON:', dynamicParams);
        console.log('DEBUG: Referral Users - paramsJson:', paramsJson);
        const parsedParams = JSON.parse(paramsJson);
        console.log('DEBUG: Referral Users - parsedParams:', parsedParams);
        result = await apiService.callDynamicAPI(selectedEndpoint.originalPath, parsedParams);
      } else {
        // Use dynamic params from form inputs for all other endpoints
        const parsedParams = JSON.parse(convertDynamicParamsToJson(effectiveParams));

        // Inject default pagination for endpoints that have no filter fields
        const noFilterEndpoints = ['Warehouse Quantity Aggregation', 'Deliver Transfer', 'invoice-photos', 'joining-requests', 'Deposit Amount Log', 'National IDs'];
        if (!userTriggered && noFilterEndpoints.includes(endpointPath) && !parsedParams.page && !parsedParams.limit) {
          // For auto-load/preview, inject a small pagination to keep response small.
          // When user explicitly requests data, do not inject pagination so the
          // backend can return the full dataset.
          parsedParams.page = 1;
          parsedParams.limit = 100;
          parsedParams.allowPagination = true;
        }

        //qr codes category
        if (endpointPath === 'User Details Per Product Per Action') {
          result = await apiService.getUserScanAnalysis(parsedParams);
        } else if (endpointPath === 'Scans per product per user type') {
          result = await apiService.getScansDetailsPerUserType(parsedParams);
        } else if (endpointPath === 'Scan Per Product details') {
          result = await apiService.getScanPerProductDetails(parsedParams);
          } else if (endpointPath === 'Warehouse Scan In/Out') {
          result = await apiService.getWarehouseScanInOutDetails(parsedParams);
          } else if (endpointPath === 'Deactivated Incentive QR') {
          result = await apiService.getDeactivatedIncentiveQR(parsedParams);
          } else if (endpointPath === 'Configuration Result Per Product') {
          result = await apiService.getConfigurationSheetCounterPerProduct(parsedParams);
          } else if (endpointPath === 'Warehouse Quantity Aggregation') {
          result = await apiService.getWarehouseQuantityAggregation(parsedParams);
          } else if (endpointPath === 'Order Scanout From Warehouse') {
          result = await apiService.getOrderScanoutFromWarehouse(parsedParams);
          
        // Wallet category endpoints
        } else if (endpointPath === 'Wallet Transactions') {
          result = await apiService.getWalletTransactionSheet(parsedParams);
        } else if (endpointPath === 'Deliver Cash') {
          result = await apiService.getDeliverCash(parsedParams);
        } else if (endpointPath === 'Deliver Transfer') {
          result = await apiService.getDeliverTransfer(parsedParams);
        } else if (endpointPath === 'Welcome Bonus') {
          result = await apiService.getWelcomeBonus(parsedParams);
          
        // Payment/Paymob endpoints
        } else if (endpointPath === 'Paymob') {
          result = await apiService.getPaymentLogs(parsedParams);
          
        // Invoice endpoints
        } else if (endpointPath === 'invoice-photos') {
          result = await apiService.getInvoicePhotosSheet(parsedParams);
          
        // Transaction Logs endpoints
        } else if (endpointPath === 'reset-logs') {
          result = await apiService.getResetLogs(parsedParams);
          
        // Joining Requests endpoints
        } else if (endpointPath === 'joining-requests') {
          result = await apiService.getJoiningRequests(parsedParams);

        // National IDs endpoints
        } else if (endpointPath === 'National IDs') {
          result = await apiService.getNationalIdsSheet(parsedParams);

        // Return System endpoints
        } else if (endpointPath === 'Return System') {
          // Convert dateType parameter as in Apps Script
          let convertedParams = { ...parsedParams };
          if (convertedParams.dateType === 'Return Date') {
            convertedParams.dateType = 'returnDate';
          } else if (convertedParams.dateType === 'Transaction Date') {
            convertedParams.dateType = 'TransactionDate';
          }
          result = await apiService.getReturnSystemScanView(convertedParams);
        } else if (endpointPath === 'Incentive') {
          // Convert dateType parameter as in Apps Script
          let convertedParams = { ...parsedParams };
          if (convertedParams.dateType === 'Return Date') {
            convertedParams.dateType = 'returnDate';
          } else if (convertedParams.dateType === 'Transaction Date') {
            convertedParams.dateType = 'TransactionDate';
          }
          result = await apiService.getReturnSystemSheet(convertedParams);
          
        // Deposit Amount endpoints
        } else if (endpointPath === 'Deposit Amount') {
          // For Deposit Amount, handle as POST operation with validation
          result = await formatDepositAmountData({ userData: [] });
        } else if (endpointPath === 'Deposit Amount Log') {
          result = await apiService.getDepositAmountOperations(parsedParams);
          
        // Gifts endpoints
        } else if (endpointPath === 'Gifts') {
          result = await apiService.getGiftsSheet(parsedParams);

        // Incentive endpoints
        } else if (endpointPath === 'End Of Month') {
          result = await apiService.getEndOfMonthSheet(parsedParams);

        } else {
          // Use dynamic API call for other endpoints
          result = await apiService.callDynamicAPI(selectedEndpoint.originalPath, parsedParams);
        }
      }
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setResponse(result.data);
      
      // Save successful data to cache
      if (selectedEndpoint) {
        setEndpointDataCache(prev => ({
          ...prev,
          [selectedEndpoint.endpoint]: {
            data: result.data,
            params: { ...dynamicParams },
            timestamp: new Date().toISOString()
          }
        }));

        // Remember the params the user actually entered for this endpoint,
        // so a later "Clear Data" + "Load Data" reloads with the same filters
        // instead of falling back to current-month defaults.
        setLastEnteredParams(prev => ({
          ...prev,
          [selectedEndpoint.endpoint]: { ...dynamicParams }
        }));
      }

      // Auto-scroll to table data after successful load
      setTimeout(() => {
        if (tableViewRef.current) {
          tableViewRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100); // Small delay to ensure DOM is updated
    } catch (err: any) {
      // Don't show error if request was cancelled
      if (err.name === 'AbortError' || err.message?.includes('aborted')) {
        return;
      }
      
      let errorMessage = 'Unable to load report data. Please try again.';
      
      // Better error messages based on the error type
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Try reducing the limit or check your connection.';
      } else if (err.response?.status === 504) {
        errorMessage = 'Server timeout. The request is taking too long. Try reducing the data limit.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later or contact support.';
      } else if (err.response?.status === 409) {
        // Handle business logic errors (Conflict)
        if (err.response?.data?.description) {
          errorMessage = `Business Rule Error: ${err.response.data.description}`;
        } else if (err.response?.data?.message) {
          errorMessage = `Business Rule Error: ${err.response.data.message}`;
        } else {
          errorMessage = 'Business rule validation failed. Please check your input parameters.';
        }
      } else if (err.response?.status === 400) {
        // Handle bad request errors
        if (err.response?.data?.description) {
          errorMessage = `Invalid Request: ${err.response.data.description}`;
        } else if (err.response?.data?.message) {
          errorMessage = `Invalid Request: ${err.response.data.message}`;
        } else {
          errorMessage = 'Invalid request parameters. Please check your input.';
        }
      } else if (err.response?.data?.description) {
        errorMessage = err.response.data.description;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      
      // Save error to cache as well
      if (selectedEndpoint) {
        setEndpointDataCache(prev => ({
          ...prev,
          [selectedEndpoint.endpoint]: {
            data: null,
            params: { ...dynamicParams },
            timestamp: new Date().toISOString(),
            error: errorMessage
          }
        }));
      }
    } finally {
      setLoading(false);
      setCurrentRequestController(null);
    }
  };

  // Clear cached data for current endpoint
  // Note: We intentionally do NOT clear `dynamicParams` here. The form values
  // stay visible to the user, and `lastEnteredParams` (saved on successful
  // load) is what `executeApi` falls back to when reloading after a clear.
  // This way:
  //   - Reports without filters: reload loads all data (no params needed).
  //   - Reports with filters: reload uses the previously entered filters.
  const clearCurrentEndpointData = () => {
    if (!selectedEndpoint) return;
    
    // Remove from cache
    setEndpointDataCache(prev => {
      const newCache = { ...prev };
      delete newCache[selectedEndpoint.endpoint];
      return newCache;
    });
    
    // Clear current display
    setResponse(null);
    setError('');
    // Keep dynamicParams so the user's filter selections remain visible.
    // They are also preserved in `lastEnteredParams` for the next Load Data.
  };



  const openTableInNewWindow = () => {
    if (!response || !selectedEndpoint) return;

    // Format the data correctly based on the endpoint
    let dataToSend = response;
    if (selectedEndpoint.endpoint === 'Users Types') {
      dataToSend = formatUsersTypesData(response);
    } else if (selectedEndpoint.endpoint === 'Users Points' && Array.isArray(response)) {
      dataToSend = formatUsersPointsData(response);
    } else if (selectedEndpoint.endpoint === 'Referral Users' && Array.isArray(response)) {
      dataToSend = formatReferralUsersData(response);
    } else if (selectedEndpoint.endpoint === 'Referred Users' && Array.isArray(response)) {
      dataToSend = formatReferredUsersData(response);
    } else if (selectedEndpoint.endpoint === 'User Details Per Product Per Action') {
      dataToSend = formatUserDetailsPerProductPerActionData(response);
    } else if (selectedEndpoint.endpoint === 'Configuration Result Per Product') {
      // Extract docs array if response is nested, otherwise use response directly
      let configData = response;
      if (response && typeof response === 'object' && !Array.isArray(response) && response.docs && Array.isArray(response.docs)) {
        configData = response.docs;
      }
      if (Array.isArray(configData)) {
        dataToSend = formatConfigurationResultPerProductData(configData);
      } else {
        dataToSend = formatConfigurationResultPerProductData([]);
      }
    } else if (selectedEndpoint.endpoint === 'Deactivated Incentive QR' && Array.isArray(response)) {
      dataToSend = formatDeactivatedIncentiveQRData(response);
    } else if (selectedEndpoint.endpoint === 'Order Scanout From Warehouse' && Array.isArray(response)) {
      dataToSend = formatOrderScanoutFromWarehouseData(response);
    } else if (selectedEndpoint.endpoint === 'Scans per product per user type' && Array.isArray(response)) {
      dataToSend = formatScansPerProductPerUserTypeData(response);
    } else if (selectedEndpoint.endpoint === 'Scan Per Product details') {
      // Extract docs array if response is nested, otherwise use response directly
      let configData = response;
      if (response && typeof response === 'object' && !Array.isArray(response) && response.docs && Array.isArray(response.docs)) {
        configData = response.docs;
      }
      if (Array.isArray(configData)) {
        dataToSend = formatScanPerProductDetailsData(configData);
      }
      
    } else if (selectedEndpoint.endpoint === 'Gifts' && (Array.isArray(response) || (response && Array.isArray(response.docs)))) {
      dataToSend = formatGiftsData(response);
    } else if (selectedEndpoint.endpoint === 'End Of Month' && (Array.isArray(response) || (response && Array.isArray(response.docs)))) {
      dataToSend = formatEndOfMonthData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Scan In/Out' && (Array.isArray(response) || (response && Array.isArray(response.docs)))) {
      dataToSend = formatWarehouseScanInOutData(response);
    } else if (selectedEndpoint.endpoint === 'Warehouse Quantity Aggregation' && Array.isArray(response)) {
      dataToSend = formatWarehouseQuantityAggregationData(response);
    } else if (selectedEndpoint.endpoint === 'Wallet Transactions') {
      let walletData = response;
      if (response && typeof response === 'object') {
        if (response.docs && Array.isArray(response.docs)) {
          walletData = response.docs;
        } else if (response.body && Array.isArray(response.body)) {
          walletData = response.body;
        } else if (response.data && Array.isArray(response.data)) {
          walletData = response.data;
        } else if (Array.isArray(response)) {
          walletData = response;
        } else {
          walletData = [];
        }
      }
      dataToSend = Array.isArray(walletData) ? formatWalletTransactionsData(walletData) : [];
    } else if (selectedEndpoint.endpoint === 'Deposit Amount') {
      // Handle Deposit Amount success message for new window
      if (response && (response.message || response.body)) {
        dataToSend = [{
          'Operation': 'Deposit Amount',
          'Status': 'Success',
          'Message': response.message || response.body?.message || 'Operation completed successfully',
          'Timestamp': new Date().toLocaleString()
        }];
      } else {
        dataToSend = response;
      }
    } else if (selectedEndpoint.endpoint === 'Paymob') {
      dataToSend = formatPaymobData(response);
    } else if (selectedEndpoint.endpoint === 'invoice-photos' && Array.isArray(response)) {
      dataToSend = formatInvoicePhotosData(response);
    } else if (selectedEndpoint.endpoint === 'Return System' && (Array.isArray(response) || (response && Array.isArray(response.docs)))) {
      dataToSend = formatReturnSystemScanViewData(response);
    } else if (selectedEndpoint.endpoint === 'Incentive' && Array.isArray(response)) {
      dataToSend = formatReturnSystemIncentiveData(response);
    } else if (selectedEndpoint.endpoint === 'joining-requests' && Array.isArray(response)) {
      dataToSend = formatJoiningRequestsData(response);
    } else if (selectedEndpoint.endpoint === 'National IDs' && (Array.isArray(response) || Array.isArray(response?.docs))) {
      dataToSend = formatNationalIdsData(response);
    } else if (selectedEndpoint.endpoint === 'reset-logs' && Array.isArray(response)) {
      dataToSend = formatResetLogsData(response);
    } else if (selectedEndpoint.endpoint === 'Deposit Amount Log' && Array.isArray(response)) {
      dataToSend = formatDepositAmountLogData(response);
    }

    // Store data in localStorage immediately for instant access
    const storageKey = `table-viewer-data-${Date.now()}`;
    const messageData = {
      data: dataToSend,
      endpoint: selectedEndpoint.endpoint,
      title: selectedEndpoint.displayName,
      timestamp: new Date().toISOString(),
      storageKey: storageKey
    };

    // Try to store in localStorage, but handle quota exceeded gracefully
    let usePostMessage = false;
    try {
      // Clean up old entries first to free up space
      const cleanupOldEntries = () => {
        const now = Date.now();
        const maxAge = 60000; // 1 minute
        
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith('table-viewer-data-')) {
            const timestamp = parseInt(key.replace('table-viewer-data-', ''));
            if (now - timestamp > maxAge) {
              localStorage.removeItem(key);
            }
          }
        }
      };
      
      cleanupOldEntries();
      
    // For large datasets, store only metadata and use postMessage
      const dataSize = JSON.stringify(messageData).length;
      console.log('[ApiExplorer] Data size:', dataSize, 'bytes');
      if (dataSize > 2000000) { // ~2MB limit for safer handling
        usePostMessage = true;
        console.log('[ApiExplorer] Data too large for localStorage, will use postMessage chunking');
        
        // Store only minimal metadata
        const metadata = {
          endpoint: selectedEndpoint.endpoint,
          title: selectedEndpoint.displayName,
          timestamp: new Date().toISOString(),
          recordCount: Array.isArray(dataToSend) ? dataToSend.length : 1,
          storageKey: storageKey,
          isLargeData: true,
          dataSize: dataSize
        };
        localStorage.setItem(storageKey, JSON.stringify(metadata));
        console.log('[ApiExplorer] Stored metadata in localStorage, dataToSend has', Array.isArray(dataToSend) ? dataToSend.length : 'N/A', 'records');
      } else {
        localStorage.setItem(storageKey, JSON.stringify(messageData));
        console.log('[ApiExplorer] Stored full data in localStorage');
      }
      
    } catch (error) {
      console.error('localStorage quota exceeded, using postMessage only:', error);
      usePostMessage = true;
    }

    // Open window with storage key in URL for instant data access
    // Include endpoint in the URL to make it more unique
    // Also include any current filter parameters from dynamicParams state
    const filterParams = new URLSearchParams();
    
    // Copy all filter parameters from dynamicParams to URL
    Object.keys(dynamicParams).forEach(key => {
      const value = dynamicParams[key];
      if (value !== null && value !== undefined && value !== '') {
        filterParams.set(key, String(value));
      }
    });
    
    console.log('[ApiExplorer] Passing filter params to new window:', dynamicParams);
    
    // Build the URL for new window including filters
    let newWindowUrl = `/table-viewer?dataKey=${storageKey}&endpoint=${encodeURIComponent(selectedEndpoint.endpoint)}&title=${encodeURIComponent(selectedEndpoint.displayName || '')}`;
    if (filterParams.toString()) {
      newWindowUrl += `&${filterParams.toString()}`;
    }
    
    const windowName = `table-viewer-${selectedEndpoint.endpoint.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
    const newWindow = window.open(
      newWindowUrl,
      windowName,
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,status=no'
    );

    if (newWindow) {
      console.log('[ApiExplorer] New window opened, passing data to it');
      
      // For large data, the new window will fetch from the API using the endpoint parameter
      // For small data, send it directly via postMessage as a fallback
      
      if (!usePostMessage) {
        // Data is small enough to send directly, do it immediately if window is ready
        setTimeout(() => {
          try {
            newWindow.postMessage(messageData, window.location.origin);
            console.log('[ApiExplorer] Data sent via postMessage');
          } catch (error) {
            console.error('[ApiExplorer] Error sending data:', error);
          }
        }, 500);
      } else {
        console.log('[ApiExplorer] Large data: new window will fetch from API using endpoint parameter');
      }
    }
  };

  return (
    <>
      <div className="api-explorer">

      {/* Main content */}
      <div className="api-console" style={{ position: 'relative' }}>
        {/* Home button — top-right of the content area */}
        <button
          onClick={() => navigate('/')}
          title="Back to reports list"
          aria-label="Go to home"
          className="home-btn"
          style={{
            position: 'absolute',
            top: '14px',
            right: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            fontSize: '14px',
            zIndex: 10,
          }}
        >
          <FontAwesomeIcon icon={faHome} />
          <span>Home</span>
        </button>

          {selectedEndpoint ? (
            <>
              <div className="console-header">
                <h2><FontAwesomeIcon icon={faCog} style={{ marginRight: '8px', color: '#666666' }} /> {selectedEndpoint.displayName || selectedEndpoint.endpoint}</h2>
                <p>{selectedEndpoint.description}</p>
                <div className="endpoint-info">
                  <span className="endpoint-category">Report Type: {selectedEndpoint.category}</span>
                </div>
              </div>

              <div className="console-body">
                <div className="input-section">
                  <div className="section-header">
                    <h3><FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px', color: '#666666' }} /> Report Filters</h3>
                  </div>
                  
                  <div className="dynamic-params">
                    {selectedEndpoint?.endpoint === 'Users Points' && (
                      <div className="performance-tip" style={{
                        backgroundColor: '#f5f5f5',
                        color: '#000000',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Users Points Data:</strong> This endpoint automatically loads ALL user production feeds with points data.<br/>
                        <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Automatic Loading:</strong> No parameters needed - system fetches all pages automatically (1000 records per page).<br/>
                        <FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Progress:</strong> Monitor the browser console and loading button for real-time progress updates.
                      </div>
                    )}
                    {selectedEndpoint?.endpoint === 'Users' && (
                      <div className="performance-tip" style={{
                        backgroundColor: '#f5f5f5',
                        color: '#000000',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Users Data:</strong> This endpoint loads all users with their complete profile information.<br/>
                        <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Ready to Load:</strong> No filters needed - system will fetch all user records automatically.<br/>
                        <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Data Includes:</strong> User profiles, contact details, location info, and account status.
                      </div>
                    )}
                    {selectedEndpoint?.endpoint === 'Users Data' && (
                      <div className="performance-tip" style={{
                        backgroundColor: '#f5f5f5',
                        color: '#000000',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <FontAwesomeIcon icon={faIdCard} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Users Data:</strong> This endpoint loads all users with their complete profile and scan activity.<br/>
                        <FontAwesomeIcon icon={faRocket} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Ready to Load:</strong> No filters needed - system will fetch all user records automatically.<br/>
                        <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '6px', color: '#666666' }} /> <strong>Data Includes:</strong> Scan history, status, segment, points, location, and joining details.
                      </div>
                    )}
                    {selectedEndpoint && getParameterConfig(selectedEndpoint.endpoint).length === 0 ? (
                      <div className="no-parameters-message" style={{
                        backgroundColor: '#f8f9fa',
                        color: '#000000',
                        padding: '20px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        border: '1px solid #dee2e6'
                      }}>
                        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px', color: '#666666' }} /> <strong>No Filters Required</strong><br/>
                        This endpoint loads data automatically without any additional parameters.
                      </div>
                    ) : (
                      selectedEndpoint && getParameterConfig(selectedEndpoint.endpoint).map((param, index, arr) => {
                        // Render paired startDate/endDate params as a single from-to range picker
                        const isDateRangeEnd = param.type === 'date' && param.name === 'endDate'
                          && arr[index - 1]?.type === 'date' && arr[index - 1]?.name === 'startDate';
                        if (isDateRangeEnd) {
                          return null;
                        }

                        const isDateRangeStart = param.type === 'date' && param.name === 'startDate'
                          && arr[index + 1]?.type === 'date' && arr[index + 1]?.name === 'endDate';
                        if (isDateRangeStart) {
                          const endParam = arr[index + 1];
                          return (
                            <div key="dateRange" className="param-group">
                              <label htmlFor={param.name}>
                                Date Range
                                {(param.required || endParam.required) && <span className="required">*</span>}
                              </label>
                              <DateRangePicker
                                startDate={typeof dynamicParams['startDate'] === 'string' ? dynamicParams['startDate'] as string : ''}
                                endDate={typeof dynamicParams['endDate'] === 'string' ? dynamicParams['endDate'] as string : ''}
                                onApply={(newStart, newEnd) => {
                                  updateDynamicParams('startDate', newStart);
                                  updateDynamicParams('endDate', newEnd);
                                }}
                              />
                            </div>
                          );
                        }

                        return (
                        <div key={param.name} className="param-group">
                          <label htmlFor={param.name}>
                            {param.label}
                            {param.required && <span className="required">*</span>}
                          </label>
                          {param.type === 'boolean' ? (
                            <input
                              id={param.name}
                              type="checkbox"
                            checked={
                              param.name === 'allowPagination' 
                                ? (dynamicParams[param.name] === undefined || dynamicParams[param.name] === true || dynamicParams[param.name] === 'true')
                                : (dynamicParams[param.name] === true || dynamicParams[param.name] === 'true')
                            }
                            onChange={(e) => updateDynamicParams(param.name, e.target.checked)}
                              className="param-checkbox"
                            />
                          ) : param.type === 'select' ? (
                            <select
                              id={param.name}
                              value={typeof dynamicParams[param.name] === 'string' ? dynamicParams[param.name] as string : ''}
                              onChange={(e) => updateDynamicParams(param.name, e.target.value)}
                              className="param-select"
                            >
                              <option value="">Select {param.label}</option>
                              {param.options?.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          ) : param.type === 'monthYear' ? (
                            <MonthYearPicker
                              label={param.label}
                              initialMonth={dynamicParams['month'] ? parseInt(dynamicParams['month'] as string) : undefined}
                              initialYear={dynamicParams['year'] ? parseInt(dynamicParams['year'] as string) : undefined}
                              initialDay={dynamicParams['day'] ? parseInt(dynamicParams['day'] as string) : undefined}
                              showDay={param.withDay ?? false}
                              onSelect={(month, year, day) => {
                                console.log('DEBUG: ApiExplorer - MonthYearPicker onSelect callback called with month:', month, 'year:', year, 'day:', day);
                                updateDynamicParams('month', month.toString());
                                updateDynamicParams('year', year.toString());
                                // Only set day if it's provided, otherwise set to empty string so it gets filtered out
                                if (day !== undefined) {
                                  updateDynamicParams('day', day.toString());
                                } else {
                                  updateDynamicParams('day', '');
                                }
                                console.log('DEBUG: ApiExplorer - Updated dynamicParams with month, year, and optional day');
                              }}
                            />
                          ) : (
                            <input
                              id={param.name}
                              type={param.type}
                              value={typeof dynamicParams[param.name] === 'string' ? dynamicParams[param.name] as string : ''}
                              onChange={(e) => updateDynamicParams(param.name, e.target.value)}
                              placeholder={param.placeholder}
                              className="param-input"
                            />
                          )}
                        </div>
                        );
                      })
                    )}
                  </div>

                  {/* Special UI for Deposit Amount endpoint */}
                  {selectedEndpoint?.endpoint === 'Deposit Amount' && (
                    <div className="deposit-transactions-section" style={{
                      marginTop: '20px',
                      padding: '20px',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      backgroundColor: '#f8f9fa'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h4 style={{ margin: '0', color: '#000000' }}>
                          <FontAwesomeIcon icon={faEdit} style={{ marginRight: '8px', color: '#666666' }} />
                          Deposit Transactions
                        </h4>
                        <div>
                          <button
                            onClick={addDepositTransaction}
                            style={{
                              backgroundColor: '#000000',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '10px'
                            }}
                          >
                            <FontAwesomeIcon icon={faPlay} style={{ marginRight: '6px' }} />
                            Add Transaction
                          </button>
                          {depositTransactions.length > 0 && (
                            <>
                              <button
                                onClick={() => {
                                  // Force a state refresh
                                  setDepositTransactions([...depositTransactions]);
                                }}
                                style={{
                                  backgroundColor: '#666666',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  marginRight: '10px'
                                }}
                              >
                                <FontAwesomeIcon icon={faRedo} style={{ marginRight: '6px' }} />
                                Refresh
                              </button>
                              <button
                                onClick={clearAllDepositTransactions}
                                style={{
                                  backgroundColor: '#999999',
                                  color: 'white',
                                  border: 'none',
                                  padding: '8px 16px',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} />
                                Clear All
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {depositTransactions.length === 0 ? (
                        <div style={{
                          textAlign: 'center',
                          padding: '40px',
                          color: '#666666',
                          fontStyle: 'italic',
                          border: '2px dashed #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: '#f8f9fa'
                        }}>
                          <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: '2em', marginBottom: '10px', color: '#999999' }} />
                          <br />
                          <strong>No transactions added yet!</strong>
                          <br />
                          Click the "Add Transaction" button above to create your first deposit transaction.
                          <br />
                          <small style={{ color: '#666666', marginTop: '10px', display: 'block' }}>
                            You need at least one transaction to send the operation.
                          </small>
                        </div>
                      ) : (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <thead>
                              <tr style={{ backgroundColor: '#000000', color: 'white' }}>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>User ID</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Transaction Type</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Deposit Amount</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Comments</th>
                                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: '100px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {depositTransactions.map((tx, index) => (
                                <tr key={tx.id} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="text"
                                      value={tx.userId}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'userId', e.target.value)}
                                      placeholder="Enter user ID"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <select
                                      value={tx.transactionType}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'transactionType', e.target.value)}
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      <option value="">Select Type</option>
                                      <option value="DEDUCTIONINCENTIVE">DEDUCTION INCENTIVE</option>
                                      <option value="CONTINUITYINCENTIVE">CONTINUITY INCENTIVE</option>
                                      <option value="EXTRAINCENTIVE">EXTRA INCENTIVE</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="number"
                                      value={tx.value}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'value', e.target.value)}
                                      placeholder="Amount"
                                      min="0"
                                      step="0.01"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                    <input
                                      type="text"
                                      value={tx.comment}
                                      onChange={(e) => updateDepositTransaction(tx.id, 'comment', e.target.value)}
                                      placeholder="Optional comments"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px'
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                                    <button
                                      onClick={() => removeDepositTransaction(tx.id)}
                                      style={{
                                        backgroundColor: '#999999',
                                        color: 'white',
                                        border: 'none',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px'
                                      }}
                                      title="Remove transaction"
                                    >
                                      <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666666' }}>
                            Total Transactions: {depositTransactions.length}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>

                <div className="action-section">
                  <button 
                    onClick={() => {
                      executeApi({ userTriggered: true });
                    }} 
                    disabled={loading || (selectedEndpoint?.endpoint === 'Deposit Amount' && depositTransactions.length === 0)}
                    className="execute-btn"
                    title={
                      selectedEndpoint?.endpoint === 'Deposit Amount' && depositTransactions.length === 0 
                        ? 'Please add at least one transaction before sending the operation' 
                        : ''
                    }
                  >
                    {loading ? (
                      (selectedEndpoint?.endpoint === 'Users Points' || selectedEndpoint?.endpoint === 'Users') && loadingProgress ? 
                        <><FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#666666' }} /> {loadingProgress}</> :
                        <><FontAwesomeIcon icon={faHourglassHalf} style={{ marginRight: '6px', color: '#666666' }} /> Loading Report...</>
                    ) : (response ? 
                        <><FontAwesomeIcon icon={faRedo} style={{ marginRight: '6px' }} /> Reload Data</> : 
                        selectedEndpoint?.endpoint === 'Deposit Amount' ? 
                          <><FontAwesomeIcon icon={faPlay} style={{ marginRight: '6px' }} /> Send Operation</> :
                          <><FontAwesomeIcon icon={faPlay} style={{ marginRight: '5px' }} /> Load Data</>
                    )}
                  </button>
                  
                  {selectedEndpoint && endpointDataCache[selectedEndpoint.endpoint] && !loading && (
                    <button 
                      onClick={clearCurrentEndpointData}
                      className="clear-btn"
                      style={{
                        marginLeft: '10px',
                        backgroundColor: '#666666',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      title="Clear cached data for this endpoint"
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ marginRight: '6px' }} /> Clear Data
                    </button>
                  )}
                  
                  {loading && (
                    <button 
                      onClick={() => {
                        if (currentRequestController) {
                          currentRequestController.abort();
                          setCurrentRequestController(null);
                          setLoading(false);
                          setError('Request cancelled by user');
                        }
                      }}
                      className="cancel-btn"
                      style={{
                        marginLeft: '10px',
                        backgroundColor: '#999999',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <FontAwesomeIcon icon={faTimes} style={{ marginRight: '6px', color: '#666666' }} /> Cancel
                    </button>
                  )}
                </div>

                {error && (
                  <div className="error-section">
                    <h3><FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px', color: '#666666' }} /> Unable to Load Report</h3>
                    <div className="error-content">
                      {error}
                    </div>
                  </div>
                )}

{(loading || response) && (
                  <>
                    {loading ? (<LoadingSpinner />) : (
                      <>
                        <div className="response-info">
                          <span>Status: <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '6px', color: '#27ae60' }} /> Data Loaded Successfully</span>
                          {selectedEndpoint && endpointDataCache[selectedEndpoint.endpoint] ? (
                            <span><FontAwesomeIcon icon={faSave} style={{ marginRight: '6px', color: '#27ae60' }} /> Cached Data - Generated: {new Date(endpointDataCache[selectedEndpoint.endpoint].timestamp).toLocaleString()}</span>
                          ) : (
                            <span>Generated: {new Date().toLocaleString()}</span>
                          )}
                          {Array.isArray(response) && (
                            <span>Total Records: {response.length}</span>
                          )}
                          {response && typeof response === 'object' && !Array.isArray(response) && response.docs && Array.isArray(response.docs) && response.docs.length > 0 && (
                            <span>Data Fields: {Object.keys(response.docs[0]).length}</span>
                          )}
                        </div>
                        
                        <div className="response-content-container">
                          <div className="table-view" ref={tableViewRef}>
                            {renderTableView(response)}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          ) : searchParams.get('report') && !endpointsLoaded ? (
            <LoadingSpinner />
          ) : (
            <div className="no-selection">
              <h2> Select a Report Type</h2>
              <p>Choose a report from the sidebar to view your data</p>

              <div className="explorer-tips">
                <h3><FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px', color: '#ffc107' }} /> How to Use:</h3>
                <ul>
                  <li>Browse report categories to find the information you need</li>
                  <li>Fill in any required filters like dates or user types</li>
                  <li>Click "Load Data" to view your report in a table format</li>
                  <li>Open reports in a new window for better viewing</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      {loading && <LoadingSpinner />}
    </>
  );
};

export default ApiExplorer;
